// src/components/StockChart.js
import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import { getIndicatorDescription } from "../indicators/indicatorMeta";

// Stock Tools toolbar (horizontal/vertical line, trendline, etc.) – load in order per Highcharts docs
import "highcharts/css/stocktools/gui.css";
import "highcharts/css/annotations/popup.css";
import "./StockToolsDarkTheme.css";

try { HighchartsMore(Highcharts); } catch {}

import AnnotationsModule from "highcharts/modules/annotations";
try { AnnotationsModule(Highcharts); } catch {}

import DragPanes from "highcharts/modules/drag-panes";
import AnnotationsAdvanced from "highcharts/modules/annotations-advanced";
import PriceIndicator from "highcharts/modules/price-indicator";
import FullScreen from "highcharts/modules/full-screen";
import StockTools from "highcharts/modules/stock-tools";

try { DragPanes(Highcharts); } catch {}
try { AnnotationsAdvanced(Highcharts); } catch {}
try { PriceIndicator(Highcharts); } catch {}
try { FullScreen(Highcharts); } catch {}
try { StockTools(Highcharts); } catch {}

// Default drawn annotations to white stroke so they're visible on dark chart
Highcharts.setOptions({
  navigation: {
    annotationsOptions: {
      shapeOptions: {
        stroke: "#ffffff",
        strokeWidth: 1,
      },
      line: {
        stroke: "#ffffff",
        strokeWidth: 1,
      },
    },
  },
});

import {
  getPrice,
  getFundamentals,
  getDCFHistory,
  getScoresSeries,
  getAnalystForecasts,
  buildAnalystForecastsFromFundamentals,
  getAnalystConsensus,
  getAaaCorporateYield,
  getCompanyLogo,
} from "../services/api";
import { buildGrahamIntrinsicPoints, buildRevenueYoYForwardPoints, buildRevenueYoYForwardPointsAnnual, buildRevenueYoYPoints, parseAaaYieldApiResponse, buildEstimateChartSlots, buildAnnualEstimateChartSlots, scaleFundamentalFieldValue, resolveMetricStatementRows, balanceSheetMetricValue, METRIC_PERIOD_QUARTER, METRIC_PERIOD_YEAR } from "../utils/fundamentalMetrics";
import { companyLogoTitleStyle, COMPANY_LOGO_TITLE_SIZE, COMPANY_LOGO_TITLE_SIZE_MOBILE } from "../utils/chartCompanyLogo";

import {
  INDICATOR_OPTIONS,
  alignDateToInterval,
  findNearestPriceValue,
  rollingBands,
  buildDivergence,
  computeFundDivRSI,
  buildSeriesCache,
  buildFScoreTTMSeries,
  getFundDate,
  getSeriesForIndicatorKey,
  forwardFillIncomeField,
  formatIndicatorValue,
  isRealizedPriceIndicator,
  VOLUME_BAR_INDICATORS,
  splitHighchartsLineBySlope,
  splitHighchartsLineByValueSign,
  computeRealizedPriceDerivative,
  REALIZED_PRICE_UP_COLOR,
  REALIZED_PRICE_DOWN_COLOR,
} from "../indicators/engine";

import ZScoreYearsModal from "./ZScoreYearsModal";
import ComboMembersModal from "./ComboMembersModal";
import {
  formatZScoreYears,
  getZScoreIndicatorLabel,
  getZScoreYearsForIndicator,
  isLegacyZScore5yKey,
  isZScoreConfigurable,
  loadZScoreYearsMap,
  normalizeZScoreIndicatorKey,
  saveZScoreYearsForIndicator,
} from "../indicators/zScoreConfig";
import {
  CLASSIC_NOVEL_COMBO_KEY,
  getComboIndicatorLabel,
  isClassicNovelCombo,
  loadComboMembers,
  saveComboMembers,
} from "../indicators/comboConfig";
import { computeCustomStrategy, computeIndicatorCalculations } from "../indicators/Indicatorcal";
import PriceForecastRegressionChart from "./PriceForecastRegressionChart";
import { FORWARD_ESTIMATE_QUARTERS, forwardEstimateRows } from "../utils/analystForecasts";

/** Verbose logging freezes Firefox devtools on large payloads; keep off in production. */
const IS_DEV = process.env.NODE_ENV === "development";

/** Yield to the browser so Fundamentals tab (correlations, etc.) stays clickable. */
function scheduleIdleTask(fn, timeout = 500) {
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => fn(), { timeout });
  } else {
    setTimeout(fn, 1);
  }
}

/* =========================
   BRAND THEME (colors, UI) - Modern Sleek Design
   ========================= */
const BRAND = {
  bg: "#0a0a0f",
  bgSecondary: "#111118",
  panel: "#1a1a24",
  panelHover: "#22222f",
  border: "#2a2a3a",
  borderLight: "#3a3a4a",
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  muted: "#888898",
  mid: "#6b6b7b",
  accent: "#6366f1",
  accentHover: "#818cf8",
  accentLight: "#a5b4fc",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
};

const surface = {
  background: BRAND.panel,
  border: `1px solid ${BRAND.border}`,
  borderRadius: 16,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)"
};

function Modal({ open, onClose, title, children, width = 560 }) {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 11000
    }}>
      <div style={{ width, maxWidth: "94vw", ...surface, boxShadow: "0 10px 30px rgba(0,0,0,0.35)" }}>
        <div style={{
          padding: "12px 16px",
          borderBottom: `1px solid ${BRAND.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          color: BRAND.text
        }}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <button onClick={onClose} style={btnGhost} aria-label="Close">✕</button>
        </div>
        <div style={{ padding: 16, color: BRAND.text }}>{children}</div>
      </div>
    </div>
  );
}

const btnBase = {
  padding: "10px 20px",
  borderRadius: 12,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "14px",
  transition: "all 0.2s ease",
  border: `1px solid ${BRAND.border}`,
};
const btn = {
  ...btnBase,
  border: `1px solid ${BRAND.border}`,
  background: BRAND.panel,
  color: BRAND.text,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
};
const btnPrimary = {
  ...btnBase,
  border: `1px solid ${BRAND.accent}`,
  background: BRAND.accent,
  color: "#ffffff",
  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
};
const btnGhost = {
  ...btnBase,
  border: `1px solid transparent`,
  background: "transparent",
  color: BRAND.text,
  padding: "6px 12px",
};

/* -----------------------------
   Local utilities (kept)
--------------------------------*/
const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
function median(arr){ if(!arr.length) return null; const a=[...arr].sort((x,y)=>x-y), m=Math.floor(a.length/2); return a.length%2?a[m]:(a[m-1]+a[m])/2; }

/** Approximate bar width for sparse fundamental points on a dense price x-axis. */
const QUARTER_MS = 90 * 24 * 60 * 60 * 1000;
function medianPointRangeMs(series) {
  if (!Array.isArray(series) || series.length < 2) return QUARTER_MS;
  const gaps = [];
  for (let i = 1; i < series.length; i++) {
    const a = series[i - 1]?.[0];
    const b = series[i]?.[0];
    if (Number.isFinite(a) && Number.isFinite(b) && b > a) gaps.push(b - a);
  }
  if (!gaps.length) return QUARTER_MS;
  gaps.sort((x, y) => x - y);
  return Math.max(gaps[Math.floor(gaps.length / 2)] * 0.65, 14 * 24 * 60 * 60 * 1000);
}

/** High-contrast colors for fundamental overlay series (screenshot-friendly). */
const METRIC_LINE_COLOR = "#ffffff";
const METRIC_FORECAST_COLOR = "#ffb020";
const AXIS_LABEL_STYLE = { color: "#ffffff", fontSize: "12px" };

/** Compact radio control — avoids oversized native radios on iOS Safari. */
function CompactRadio({ checked, name, onChange, children, style }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        color: BRAND.text,
        ...style,
      }}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0, margin: 0 }}
      />
      <span
        aria-hidden
        style={{
          width: 14,
          height: 14,
          boxSizing: "border-box",
          flexShrink: 0,
          borderRadius: "50%",
          border: `1.5px solid ${checked ? BRAND.accent : BRAND.borderLight}`,
          background: checked ? BRAND.accent : "transparent",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {checked ? (
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />
        ) : null}
      </span>
      {children}
    </label>
  );
}

/** Safely push x-axis max for estimate/forecast modes (chart may remount mid-timeout). */
function extendXAxisMax(chart, xAxisMax) {
  const xAxis = chart?.xAxis?.[0];
  if (!xAxis || !Number.isFinite(xAxisMax)) return;
  try {
    const extremes = xAxis.getExtremes();
    if (!extremes.max || xAxisMax > extremes.max) {
      xAxis.setExtremes(extremes.min, xAxisMax, false, false);
    }
  } catch {
    // Ignore: chart destroyed or axes not ready yet
  }
}

/** Force annotation paths/lines to thin white stroke (0.25px) so they stay visible on dark chart; skip tracker path so hit area stays wide */
function thinAnnotationStrokes(chart) {
  if (!chart?.container?.querySelectorAll) return;
  const paths = chart.container.querySelectorAll("[class*='annotation-shapes'] path:not(.highcharts-tracker-line), [class*='annotation-shapes'] line:not(.highcharts-tracker-line)");
  paths.forEach((path) => {
    path.setAttribute("stroke-width", "0.25");
    path.setAttribute("stroke", "#ffffff");
  });
}

/* -----------------------------
   Highlight rules for indicator
--------------------------------*/
const GREEN = "rgba(34,197,94,0.18)";
const RED   = "rgba(239,68,68,0.18)";

const HILITE_RULES = {
  ev_ebitda: [{ dir: "above", thr: 3, color: GREEN, label: "EVY>3" }],
  ev_ebitda_z: [
    { dir: "above", thr: 1, color: GREEN, label: "EV yield z>1" },
    { dir: "below", thr: -1, color: RED, label: "EV yield z<-1" },
  ],
  div_yield_z: [
    { dir: "above", thr: 1, color: GREEN, label: "Div yield z>1" },
    { dir: "below", thr: -1, color: RED, label: "Div yield z<-1" },
  ],
  pb_ratio:   [ { dir:"below", thr:-1, color:GREEN, label:"P/B z<-1 (DCA)" }, { dir:"above", thr:1, color:RED, label:"P/B z>1 (Sell)" } ],
  pe_ratio:   [ { dir:"below", thr:-1, color:GREEN, label:"P/E z<-1 (DCA)" }, { dir:"above", thr:1, color:RED, label:"P/E z>1 (Sell)" } ],
  poi_ratio:   [ { dir:"below", thr:-1, color:GREEN, label:"P/OI z<-1 (DCA)" }, { dir:"above", thr:1, color:RED, label:"P/OI z>1 (Sell)" } ],
  ps_ratio:   [ { dir:"below", thr:-1,  color:GREEN, label:"PS z<-1 (Below Avg)" }, { dir:"above", thr:1, color:RED, label:"PS z>1 (Above Avg)" } ],
  cf_price:   [ { dir:"below", thr:-1, color:GREEN, label:"P/CF z<-1 (DCA)" }, { dir:"above", thr:1, color:RED, label:"P/CF z>1 (Sell)" } ],
  cnf_combo:  [ { dir:"below", thr:-1, color:GREEN, label:"Combo z<-1 (cheap)" }, { dir:"above", thr:1, color:RED, label:"Combo z>1 (rich)" } ],
  peg_ratio:  [ { dir:"below", thr:30,  color:GREEN, label:"PEGpct<30" }, { dir:"above", thr:70, color:RED, label:"PEGpct>70" } ],
  fgpz:       [ { dir:"below", thr:-1, color:GREEN, label:"FGPZ<-1 (cheap vs growth)" }, { dir:"above", thr:1, color:RED, label:"FGPZ>1 (rich vs growth)" } ],
  gag:        [ { dir:"above", thr:0, color:GREEN, label:"GAG>0 (growth underpriced)" }, { dir:"below", thr:0, color:RED, label:"GAG<0 (growth overpriced)" } ],
  mean_rev:   [ { dir:"below", thr:-1,  color:GREEN, label:"z<-1" }, { dir:"above", thr:1,  color:RED,  label:"z>1" } ],
  syo:        [ { dir:"above", thr:50,  color:GREEN, label:"SYO>50" }, { dir:"below", thr:50, color:RED, label:"SYO<50" } ],
  rmo:        [ { dir:"above", thr:10,  color:GREEN, label:"RMO>10" }, { dir:"below", thr:-10,color:RED, label:"RMO<-10" } ],
  tamo:       [ { dir:"above", thr:30,  color:GREEN, label:"TAMO>30" }, { dir:"below", thr:-30,color:RED, label:"TAMO<-30" } ],
  nimo:       [ { dir:"above", thr:30,  color:GREEN, label:"NIMO>30" }, { dir:"below", thr:-30,color:RED, label:"NIMO<-30" } ],
  vmz:        [ { dir:"below", thr:0,   color:GREEN, label:"VMZ<0" },  { dir:"above", thr:0,  color:RED,  label:"VMZ>0" } ],
  oey:        [ { dir:"above", thr:70,  color:GREEN, label:"OEY>70" }, { dir:"below", thr:30, color:RED, label:"OEY<30" } ],
  vmo:        [ { dir:"below", thr:-0.5,color:GREEN, label:"VMO<-0.5" }, { dir:"above", thr:0.5, color:RED, label:"VMO>0.5" } ],
  mrvz:       [ { dir:"below", thr:-1.5,color:GREEN, label:"MRVZ<-1.5" }, { dir:"above", thr:1.5, color:RED, label:"MRVZ>1.5" } ],
  vddm:       [ { dir:"below", thr:-20, color:GREEN, label:"VDDM<-20 (Buy)" }, { dir:"above", thr:20, color:RED, label:"VDDM>20 (Sell)" } ],
  nupl:       [ { dir:"below", thr:-10, color:GREEN, label:"NUPL<-10" }, { dir:"above", thr:10, color:RED, label:"NUPL>10" } ],
  risk:       [ { dir:"below", thr:30,  color:GREEN, label:"Risk<30" }, { dir:"above", thr:70, color:RED, label:"Risk>70" } ],
  srm:        [ { dir:"above", thr:70,  color:GREEN, label:"SRM>70" },  { dir:"below", thr:30, color:RED, label:"SRM<30" } ],
  pe_peratio: [ { dir:"above", thr:1.2, color:GREEN, label:"P/E Ratio>1.2" }, { dir:"below", thr:0.8, color:RED, label:"P/E Ratio<0.8" } ],
};

function buildRevValShading(line, bounds) {
  if (!Array.isArray(line) || !line.length || !Array.isArray(bounds) || !bounds.length) return [];
  const bMap = new Map(bounds.map(b => [b.time, b]));
  const greenArr = [], redArr = [];
  for (const [t, vRaw] of line) {
    const v = Number(vRaw);
    const tSec = t / 1000;
    const b = bMap.get(tSec);
    if (!b || !Number.isFinite(v)) {
      greenArr.push([t, null, null]);
      redArr.push([t, null, null]);
      continue;
    }
    if (v < b.low) {
      greenArr.push([t, v, b.low]);
      redArr.push([t, null, null]);
    } else if (v > b.high) {
      greenArr.push([t, null, null]);
      redArr.push([t, b.high, v]);
    } else {
      greenArr.push([t, null, null]);
      redArr.push([t, null, null]);
    }
  }
  const out = [];
  if (greenArr.some(p => p[1] != null)) {
    out.push({
      type: "arearange",
      name: "Undervalued (price < 95% CI low)",
      data: greenArr,
      connectNulls: false,
      yAxis: 1,
      lineWidth: 0,
      marker: { enabled: false },
      enableMouseTracking: false,
      color: GREEN,
      fillColor: GREEN,
      fillOpacity: 0.18,
      dataGrouping: { enabled: false },
      zIndex: 0
    });
  }
  if (redArr.some(p => p[1] != null)) {
    out.push({
      type: "arearange",
      name: "Overvalued (price > 95% CI high)",
      data: redArr,
      connectNulls: false,
      yAxis: 1,
      lineWidth: 0,
      marker: { enabled: false },
      enableMouseTracking: false,
      color: RED,
      fillColor: RED,
      fillOpacity: 0.18,
      dataGrouping: { enabled: false },
      zIndex: 0
    });
  }
  return out;
}

function buildShadingForRules(line, rules) {
  if (!Array.isArray(line) || !line.length || !Array.isArray(rules) || !rules.length) return [];
  const out = [];
  for (const r of rules) {
    const arr = [];
    for (const [t, vRaw] of line) {
      const v = Number(vRaw);
      if (!Number.isFinite(v)) { arr.push([t, null, null]); continue; }
      if (r.dir === "above") arr.push(v > r.thr ? [t, r.thr, v] : [t, null, null]);
      else arr.push(v < r.thr ? [t, v, r.thr] : [t, null, null]);
    }
    out.push({
      type: "arearange",
      name: r.label,
      data: arr,
      yAxis: 1,
      lineWidth: 0,
      marker: { enabled: false },
      enableMouseTracking: false,
      color: r.color,
      fillColor: r.color,
      fillOpacity: 0.18,
      dataGrouping: { enabled: false },
      zIndex: 0
    });
  }
  return out;
}

function hiliteRulesForIndicator(indicator) {
  const key = normalizeZScoreIndicatorKey(indicator);
  return HILITE_RULES[key] || [];
}

function buildBottomPlotLines(indicator) {
  if (isRealizedPriceIndicator(indicator)) return [{ value: 0, color: BRAND.border, width: 1, zIndex: 1 }];
  if (indicator === "rev_val" || indicator === "eps_val") return [{ value: 1, color: BRAND.border, width: 1, zIndex: 1 }];
  if (indicator === "forward_pe" || indicator === "ttm_pe" || indicator === "revenue_cagr" || indicator === "eps_cagr" || indicator === "gag") return [];
  const rules = hiliteRulesForIndicator(indicator);
  const uniq = Array.from(new Set(rules.map(r => r.thr))).sort((a,b)=>a-b);
  if (!uniq.length) return [{ value: 0, color: BRAND.border, width: 1, zIndex: 1 }];
  return uniq.map(v => ({ value: v, color: BRAND.border, width: 1, zIndex: 1 }));
}

/* -----------------------------
   Helpers
--------------------------------*/
function aggregateToInterval(data, interval) {
  if (interval === "daily") {
    return (data || [])
      .filter(p => p && p.date && p.close != null && isFinite(p.close))
      .map(({ date, close }) => {
        const [Y, M, D] = date.split("-").map(Number);
        return { time: Date.UTC(Y, M - 1, D) / 1000, value: close };
      })
      .sort((a, b) => a.time - b.time);
  }
  const grouped = {};
  (data || [])
    .filter(p => p && p.date && p.close != null && isFinite(p.close))
    .forEach(({ date, close }) => {
      const key = alignDateToInterval(date, interval);
      (grouped[key] = grouped[key] || []).push(close);
    });
  return Object.entries(grouped)
    .map(([timeStr, prices]) => {
      const [Y, M, D] = timeStr.split("-").map(Number);
      return {
        time: Date.UTC(Y, M - 1, D) / 1000,
        value: prices.reduce((s, v) => s + v, 0) / prices.length,
      };
    })
    .sort((a, b) => a.time - b.time);
}

function calculateCorrelation(metricPoints, pricePoints) {
  const aligned = metricPoints
    .map(p => {
      const y = findNearestPriceValue(p.time, pricePoints);
      return y != null ? [p.value, y] : null;
    })
    .filter(Boolean);
  if (aligned.length < 2) return null;
  const xs = aligned.map(a => a[0]);
  const ys = aligned.map(a => a[1]);
  const mean = arr => arr.reduce((s, v) => s + v, 0) / arr.length;
  const mX = mean(xs), mY = mean(ys);
  const cov = xs.reduce((s, x, i) => s + (x - mX) * (ys[i] - mY), 0);
  const sd = arr => Math.sqrt(arr.reduce((s, v) => s + (v - mean(arr)) ** 2, 0));
  const sX = sd(xs), sY = sd(ys);
  return (sX && sY) ? cov / (sX * sY) : null;
}

/** Multiple linear regression Price ~ EPS + Revenue_B with 95% prediction interval.
 *  rows: [{ eps, revB, price }]. Returns predict(eps, revB) => { point, low, high } or null. */
function multipleLinearRegressionWithPI(rows) {
  if (!rows || rows.length < 6) return null;
  const n = rows.length;
  const X = rows.map(r => [1, r.eps, r.revB]);
  const Y = rows.map(r => r.price);
  // X'X (3x3)
  const XtX = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      XtX[i][j] = X.reduce((s, row) => s + row[i] * row[j], 0);
    }
  }
  // X'Y (3x1)
  const XtY = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    XtY[i] = X.reduce((s, row, k) => s + row[i] * Y[k], 0);
    XtX[i] = XtX[i].slice();
  }
  // Invert 3x3 via cofactors
  const det = (m) =>
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1])
    - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0])
    + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  const d = det(XtX);
  if (Math.abs(d) < 1e-12) return null;
  const inv = [
    [(XtX[1][1] * XtX[2][2] - XtX[1][2] * XtX[2][1]) / d, (XtX[0][2] * XtX[2][1] - XtX[0][1] * XtX[2][2]) / d, (XtX[0][1] * XtX[1][2] - XtX[0][2] * XtX[1][1]) / d],
    [(XtX[1][2] * XtX[2][0] - XtX[1][0] * XtX[2][2]) / d, (XtX[0][0] * XtX[2][2] - XtX[0][2] * XtX[2][0]) / d, (XtX[0][2] * XtX[1][0] - XtX[0][0] * XtX[1][2]) / d],
    [(XtX[1][0] * XtX[2][1] - XtX[1][1] * XtX[2][0]) / d, (XtX[0][1] * XtX[2][0] - XtX[0][0] * XtX[2][1]) / d, (XtX[0][0] * XtX[1][1] - XtX[0][1] * XtX[1][0]) / d],
  ];
  const beta = [0, 0, 0];
  for (let i = 0; i < 3; i++) beta[i] = inv[i].reduce((s, v, j) => s + v * XtY[j], 0);
  const pred = (eps, revB) => beta[0] + beta[1] * eps + beta[2] * revB;
  const residuals = Y.map((y, i) => y - pred(X[i][1], X[i][2]));
  const mse = residuals.reduce((s, r) => s + r * r, 0) / (n - 3);
  const se = Math.sqrt(mse);
  const t95 = 1.96;
  return (eps, revB) => {
    const x0 = [1, eps, revB];
    let x0InvXtXx0 = 0;
    for (let i = 0; i < 3; i++)
      for (let j = 0; j < 3; j++)
        x0InvXtXx0 += x0[i] * inv[i][j] * x0[j];
    const sePred = se * Math.sqrt(1 + x0InvXtXx0);
    const point = pred(eps, revB);
    return { point, low: point - t95 * sePred, high: point + t95 * sePred };
  };
}

/** Univariate OLS with 95% prediction interval. pairs: [[x,y],...]. */
function fitLinearRegressionWithPI(pairs) {
  if (!pairs || pairs.length < 6) return null;
  const n = pairs.length;
  const sumX = pairs.reduce((s, p) => s + p[0], 0);
  const sumY = pairs.reduce((s, p) => s + p[1], 0);
  const sumXY = pairs.reduce((s, p) => s + p[0] * p[1], 0);
  const sumX2 = pairs.reduce((s, p) => s + p[0] * p[0], 0);
  const meanX = sumX / n;
  const meanY = sumY / n;
  const ssXX = sumX2 - n * meanX * meanX;
  const ssXY = sumXY - n * meanX * meanY;
  if (ssXX === 0) return null;
  const slope = ssXY / ssXX;
  const intercept = meanY - slope * meanX;
  const residuals = pairs.map((p) => p[1] - (intercept + slope * p[0]));
  const mse = residuals.reduce((s, r) => s + r * r, 0) / (n - 2);
  const se = Math.sqrt(mse);
  const t95 = 1.96;
  const predict = (x) => {
    const point = intercept + slope * x;
    const xDiff = x - meanX;
    const sePred = se * Math.sqrt(1 + 1 / n + (xDiff * xDiff) / ssXX);
    return { point, low: point - t95 * sePred, high: point + t95 * sePred };
  };
  const buildChartLines = (extraX = []) => {
    const allX = [...pairs.map((p) => p[0]), ...extraX.filter((x) => x != null && x > 0)];
    if (!allX.length) return { regressionLine: [], ciBand: [] };
    const xMin = Math.min(...allX);
    const xMax = Math.max(...allX);
    const pad = (xMax - xMin) * 0.05 || Math.abs(xMin) * 0.05 || 0.1;
    const xLo = xMin - pad;
    const xHi = xMax + pad;
    const steps = 60;
    const regressionLine = [];
    const ciBand = [];
    for (let i = 0; i <= steps; i++) {
      const x = xLo + ((xHi - xLo) * i) / steps;
      const pred = predict(x);
      regressionLine.push([x, pred.point]);
      ciBand.push([x, pred.low, pred.high]);
    }
    return { regressionLine, ciBand };
  };
  return { predict, buildChartLines };
}

/** Univariate linear regression with 95% PI. pairs: [[x,y],...]. Returns predict(x) => { point, low, high } or null. */
function linearRegressionWithPI(pairs) {
  const fit = fitLinearRegressionWithPI(pairs);
  return fit ? fit.predict : null;
}

/* -----------------------------
   Metric options (kept)
--------------------------------*/
const METRIC_OPTIONS = {
  none:          { label: "None",                         source: "none" },
  revenue:       { label: "Revenue",                      source: "income",    field: "revenue",                  unit: "$B" },
  revenue_yoy:   { label: "YoY revenue growth",           source: "revenue_yoy",                                             unit: "%"  },
  revenue_yoy_forecast: { label: "YoY Rev growth (Forecast)", source: "revenue_yoy_forecast",                               unit: "%"  },
  eps:           { label: "EPS",                          source: "income",    field: "eps",                      unit: "$"  },
  eps_estimate:  { label: "EPS (Forecast)", source: "combined", field: "epsEstimate",       unit: "$"  },
  revenue_estimate: { label: "Revenue (Forecast)", source: "combined", field: "revenueEstimate", unit: "$B" },
  fcf:           { label: "Free Cash Flow",               source: "cashflow",  field: "freeCashFlow",            unit: "$M" },
  ocf:           { label: "Operating Cash Flow",          source: "cashflow",  field: "operatingCashFlow",       unit: "$M" },
  roic:          { label: "ROIC",                         source: "metrics",   field: "returnOnCapitalEmployed", unit: "%"  },
  gross_margin:  { label: "Gross Margin",                 source: "metrics",   field: "grossProfitMargin",       unit: "%"  },
  operating_margin:{label:"Operating Margin",             source: "metrics",   field: "operatingProfitMargin",   unit: "%"  },
  net_profit_margin:{label:"Net Profit Margin",            source: "metrics",   field: "netProfitMargin",         unit: "%"  },
  cashpershare:  { label: "Cash per share",               source: "metrics",   field: "cashPerShare",            unit: "$"  },
  roa:           { label: "ROA",                          source: "metrics",   field: "returnOnAssets",          unit: "%"   },
  dcf:           { label: "DCF Valuation (per share)",    source: "dcf",       field: "dcfPerShare",             unit: "$"  },
  graham_intrinsic: { label: "Graham intrinsic value",     source: "graham",    field: "",                        unit: "$"  },
  current_ratio: { label: "Current Ratio",                source: "metrics",    field: "currentRatio",            unit: ""   },
  operating_income: { label: "Operating Income",          source: "income",    field: "operatingIncome",        unit: "$B" },
  shares_outstanding: { label: "Shares Outstanding",      source: "income",    field: "weightedAverageShsOut",  fieldAlt: "weightedAverageShsOutDil", unit: "M" },
  debt:          { label: "Debt",                         source: "balanceSheet", field: "totalDebt",          unit: "$B" },
  debt_equity:   { label: "Debt / Equity",                source: "balanceSheet", field: "debtToEquity",       unit: "x"  },
  debt_assets:   { label: "Debt / Assets",                source: "balanceSheet", field: "debtToAssets",       unit: "x"  },
};

/**
 * Build overlay metric / estimate / DCF point series from cached fundamentals (no network).
 * Used so Fundamentals/Overview can switch metrics without re-fetching.
 */
function computeMetricOverlayPoints({
  fundamentals,
  metricKey,
  metricPeriod = METRIC_PERIOD_QUARTER,
  pxSec,
  interval,
  lagDays,
  dcfRes,
  aaaYieldPoints = [],
  aaaFallbackPct = 5,
}) {
  const minPxTime = pxSec?.length ? pxSec[0].time : Number.POSITIVE_INFINITY;
  const minPxTimeForMetrics = pxSec?.length ? pxSec[0].time : 0;
  const isAnnual = metricPeriod === METRIC_PERIOD_YEAR;
  const cropToPrice = (arr = []) =>
    (arr || []).filter(p => p && Number.isFinite(p.time) && p.time >= minPxTime);

  function shiftAndMap(arr, valueSelector) {
    return cropToPrice(
      (arr || [])
        .map(e => {
          const d = new Date(e.fillingDate || e.acceptedDate || e.date);
          d.setDate(d.getDate() + lagDays);
          const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
          const [Y, M, D] = key.split("-").map(Number);
          const time = Date.UTC(Y, M - 1, D) / 1000;
          const val = valueSelector(e);
          return (val != null && isFinite(val)) ? { time, value: val } : null;
        })
        .filter(Boolean)
        .sort((a,b)=>a.time-b.time)
    );
  }
  function enforceStrictAsc(arr = []) {
    const m = new Map();
    for (const p of arr) {
      if (p && Number.isFinite(p.time) && Number.isFinite(p.value)) m.set(p.time, p.value);
    }
    return Array.from(m.entries()).sort((a,b)=>a[0]-b[0]).map(([time,value])=>({ time, value }));
  }

  const opt = METRIC_OPTIONS[metricKey];
  let mPts = [];
  let estPts = [];
  let dcfPts = [];

  if (!opt || opt.source === "none") {
    return { mPts, estPts, dcfPts };
  }
  if (metricKey === "revenue_yoy") {
    const incomeRows = isAnnual
      ? (fundamentals.annualIncome || fundamentals.income || [])
      : (fundamentals.income || []);
    const pts = buildRevenueYoYPoints(incomeRows, interval, lagDays, minPxTimeForMetrics, metricPeriod);
    mPts = cropToPrice(enforceStrictAsc(pts));
    return { mPts, estPts, dcfPts };
  }
  if (metricKey === "revenue_yoy_forecast") {
    const incomeRows = isAnnual
      ? (fundamentals.annualIncome || fundamentals.income || [])
      : (fundamentals.income || []);
    const actualYoY = buildRevenueYoYPoints(
      incomeRows,
      interval,
      lagDays,
      minPxTimeForMetrics,
      metricPeriod
    );
    mPts = cropToPrice(enforceStrictAsc(actualYoY));
    const lastActualTime = mPts.length ? mPts[mPts.length - 1].time : null;
    let fwdPts = enforceStrictAsc(
      isAnnual
        ? buildRevenueYoYForwardPointsAnnual(fundamentals, {
            interval,
            lagDays,
            minPxTime: minPxTimeForMetrics,
            minForwardTime: lastActualTime,
          })
        : buildRevenueYoYForwardPoints(fundamentals, {
            interval,
            lagDays,
            minPxTime: minPxTimeForMetrics,
            minForwardTime: lastActualTime,
          })
    );
    // Bridge last actual onto the forecast line only when spacing is ~quarterly
    // (avoids Jul filing-date actual sitting ~2 months before Sep fiscal-end forecast).
    const MIN_BRIDGE_GAP_SEC = (isAnnual ? 200 : 75) * 24 * 3600;
    if (mPts.length && fwdPts.length) {
      const lastActual = mPts[mPts.length - 1];
      const later = fwdPts.filter((p) => p.time > lastActual.time);
      if (later.length && later[0].time - lastActual.time >= MIN_BRIDGE_GAP_SEC) {
        fwdPts = enforceStrictAsc([lastActual, ...later]);
      } else {
        fwdPts = enforceStrictAsc(later);
      }
    }
    estPts = fwdPts;
    return { mPts, estPts, dcfPts };
  }
  if (opt.source === "combined") {
    // Historical actuals: income statement on filing/report dates (source of truth).
    // Estimate line: earnings-calendar + forward analyst rows, placed on report dates too.
    const isRevenue = metricKey === "revenue_estimate";
    const income = resolveMetricStatementRows(fundamentals, "income", metricPeriod);
    const actual = shiftAndMap(income, (e) => {
      if (isRevenue) {
        return e.revenue != null && isFinite(e.revenue) ? e.revenue / 1e9 : undefined;
      }
      return e.eps != null && isFinite(e.eps) ? e.eps : undefined;
    });

    const slots = isAnnual
      ? buildAnnualEstimateChartSlots(
          fundamentals,
          isRevenue ? "revenue" : "eps",
          interval,
          lagDays,
          minPxTimeForMetrics
        )
      : buildEstimateChartSlots(
          fundamentals,
          isRevenue ? "revenue" : "eps",
          interval,
          lagDays,
          minPxTimeForMetrics
        );
    const estimates = slots
      .map((s) => {
        if (s.estimate == null || !Number.isFinite(s.estimate)) return null;
        const value = isRevenue ? s.estimate / 1e9 : s.estimate;
        return Number.isFinite(value) ? { time: s.time, value } : null;
      })
      .filter(Boolean);

    mPts = enforceStrictAsc(cropToPrice(actual));
    estPts = enforceStrictAsc(cropToPrice(estimates));
    return { mPts, estPts, dcfPts };
  }
  if (opt.source === "graham") {
    mPts = buildGrahamIntrinsicPoints(fundamentals, {
      interval,
      lagDays,
      minPxTime: minPxTimeForMetrics,
      maxPxTime: pxSec?.length ? pxSec[pxSec.length - 1].time : null,
      aaaYieldPoints: aaaYieldPoints || [],
      fallbackYieldPercent: aaaFallbackPct ?? 5,
    });
    mPts = cropToPrice(mPts);
    return { mPts, estPts, dcfPts };
  }
  if (opt.source === "dcf") {
    dcfPts = cropToPrice(
      (dcfRes || [])
        .map(e => {
          const d = new Date(e.fillingDate || e.acceptedDate || e.date);
          d.setDate(d.getDate() + lagDays);
          const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
          const [Y,M,D] = key.split("-").map(Number);
          const t = Date.UTC(Y, M-1, D)/1000;
          const v = e.dcfPerShare ?? e.dcf;
          return (v != null) ? { time:t, value:v } : null;
        })
        .filter(Boolean)
        .sort((a,b)=>a.time-b.time)
    );
    mPts = dcfPts;
    return { mPts, estPts, dcfPts };
  }
  const raw = resolveMetricStatementRows(fundamentals, opt.source, metricPeriod);
  const pts = shiftAndMap(raw, e => {
    if (opt.source === "balanceSheet") return balanceSheetMetricValue(e, opt.field);
    const rawVal = e[opt.field] ?? (opt.fieldAlt ? e[opt.fieldAlt] : undefined);
    return scaleFundamentalFieldValue(opt.field, rawVal);
  });
  mPts = pts;
  return { mPts, estPts, dcfPts };
}

/* -----------------------------
   Indicator Categories
--------------------------------*/
const INDICATOR_CATEGORIES = {
  "Novel Fundamental": [
    "syo",      // SYO (Composite Yield Oscillator)
    "rmo",      // RMO (Revenue Momentum Oscillator)
    "tamo",     // TAMO (Total Assets Momentum Oscillator)
    "nimo",     // NIMO (Net Income Momentum Oscillator)
    "vmz",      // VMZ (Value-Momentum Z-Score)
    "vmo",      // Valuation Momentum (Δz)
    "pe_peratio", // P/E Ratio Comparison (Historical/Forward)
    "fgpz",       // Forward Growth Premium Z
    "gag",        // Growth Acceleration Gap
  ],
  "Classic Fundamental": [
    "rev_val",  // Revenue Valuation (Price vs Revenue-Based Fair Value)
    "eps_val",  // EPS Valuation (Price vs EPS-Based Fair Value)
    "ev_ebitda", // EV/EBITDA Yield (raw)
    "div_yield", // Dividend Yield (TTM div / price)
    "peg_ratio", // PEG Ratio
    "forward_pe", // Forward P/E (NTM consensus)
    "ttm_pe", // TTM P/E (trailing EPS)
    "revenue_cagr", // Revenue CAGR (1y & 2y TTM)
    "eps_cagr", // EPS CAGR (1y & 2y TTM)
    "oey",      // Owner Earnings Yield
  ],
  "Classic / Novel Fundamental": [
    "cnf_combo",
    "div_yield_z",
    "pb_ratio",
    "pe_ratio",
    "poi_ratio",
    "ps_ratio",
    "cf_price",
    "ev_ebitda_z",
  ],
  "Price Action Based": [
    "mean_rev", // Mean reversion
    "srm",      // SRM (Risk Metric)
  ],
  "Novel Technical": [
    "mrvz",     // MRVZ (Turnover-based MVRV-like Z)
    "vddm",     // VDD Multiple (Value Days Destroyed)
    "nupl",     // NUPL (equity proxy)
    "realized_price_252", // Realized Price (252d VWAC)
    "realized_price_504", // Realized Price (504d VWAC)
    "realized_price_5y",  // Realized Price (5y VWAC)
    "risk",     // Composite Risk (0–100)
  ],
};

/* -----------------------------
   Modals
--------------------------------*/
function IndicatorsModal({ open, onClose, indicator, onPickIndicator, zScoreYearsMap, comboMembers }) {
  const activeBase = normalizeZScoreIndicatorKey(indicator);
  return (
    <Modal open={open} onClose={onClose} title="Indicators" width={700}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:16 }}>
        <div>
          <div style={{ fontWeight:600, marginBottom:8, color: BRAND.text }}>Indicators by Category</div>
          <div style={{ fontSize:13, color:BRAND.muted, marginBottom:8 }}>
            Z-score indicators prompt for a custom lookback (years, decimals allowed).
            Combined Classic/Novel lets you pick which valuation Z-scores to average.
          </div>
          <div style={{ maxHeight:400, overflow:"auto", border:`1px solid ${BRAND.border}`, borderRadius:8, padding:8 }}>
            {Object.entries(INDICATOR_CATEGORIES).map(([category, indicatorKeys]) => (
              <div key={category} style={{ marginBottom:16 }}>
                <div style={{
                  fontWeight:600,
                  marginBottom:6,
                  color: BRAND.accent,
                  fontSize:14,
                  paddingBottom:4,
                  borderBottom:`1px solid ${BRAND.border}`
                }}>
                  {category}
                </div>
                {indicatorKeys.map((k) => {
                  if (!INDICATOR_OPTIONS[k] && !isZScoreConfigurable(k) && !isClassicNovelCombo(k)) return null;
                  const label = isClassicNovelCombo(k)
                    ? (activeBase === k || indicator === k
                        ? getComboIndicatorLabel(comboMembers)
                        : (INDICATOR_OPTIONS[k] || getComboIndicatorLabel(comboMembers)))
                    : isZScoreConfigurable(k)
                    ? getZScoreIndicatorLabel(k, getZScoreYearsForIndicator(k, zScoreYearsMap))
                    : INDICATOR_OPTIONS[k];
                  if (!label) return null;
                  return (
                    <CompactRadio
                      key={k}
                      name="indicator"
                      checked={activeBase === k || indicator === k}
                      onChange={() => onPickIndicator(k)}
                      style={{ padding: "4px 6px", marginLeft: 8 }}
                    >
                      <span>{label}</span>
                    </CompactRadio>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end", gap:8 }}>
        <button style={btn} onClick={onClose}>Close</button>
        <button style={btnPrimary} onClick={onClose}>Apply</button>
      </div>
    </Modal>
  );
}

function MetricsModal({ open, onClose, metricKey, setMetricKey, metricDisplayType, setMetricDisplayType }) {
  return (
    <Modal open={open} onClose={onClose} title="Fundamental Metrics">
      <div style={{ columns: 2, columnGap: 24, maxHeight: 360, overflow:"auto" }}>
        {Object.entries(METRIC_OPTIONS)
          .filter(([k]) => k !== "dcf")
          .map(([k, o]) => (
          <div key={k} style={{ breakInside:"avoid", marginBottom:8 }}>
            <CompactRadio
              name="metric"
              checked={metricKey === k}
              onChange={() => setMetricKey(k)}
            >
              <span>{o.label}</span>
            </CompactRadio>
          </div>
        ))}
      </div>
      <div style={{ marginTop:16, paddingTop:12, borderTop:`1px solid ${BRAND.border}` }}>
        <div style={{ fontWeight:600, marginBottom:8 }}>Chart style</div>
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          <CompactRadio
            name="metricDisplayType"
            checked={metricDisplayType === "line"}
            onChange={() => setMetricDisplayType("line")}
          >
            <span>Line</span>
          </CompactRadio>
          <CompactRadio
            name="metricDisplayType"
            checked={metricDisplayType === "column"}
            onChange={() => setMetricDisplayType("column")}
          >
            <span>Bar</span>
          </CompactRadio>
        </div>
      </div>
      <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end", gap:8 }}>
        <button style={btn} onClick={onClose}>Close</button>
        <button style={btnPrimary} onClick={onClose}>Apply</button>
      </div>
    </Modal>
  );
}

function CustomStrategyModal({ open, onClose, indicator, indicatorSeries, pricePoints, onResult }) {
  const [strategyType, setStrategyType] = useState('dca');
  const [entryThreshold, setEntryThreshold] = useState('');
  const [exitThreshold, setExitThreshold] = useState('');
  const [entryOperator, setEntryOperator] = useState('<');
  const [exitOperator, setExitOperator] = useState('>=');
  const [entryCrossoverDir, setEntryCrossoverDir] = useState('up'); // 'up' or 'down' for crossover
  const [exitCrossoverDir, setExitCrossoverDir] = useState('down'); // 'up' or 'down' for crossover
  const [exitMode, setExitMode] = useState('indicator'); // 'indicator' | 'holding'
  const [holdingDays, setHoldingDays] = useState('');
  const [maxDays, setMaxDays] = useState('');

  const handleRun = () => {
    const entryTh = parseFloat(entryThreshold);
    const exitTh = parseFloat(exitThreshold);
    const maxD = exitMode === 'indicator' && maxDays ? parseInt(maxDays, 10) : null;
    const holdD = exitMode === 'holding' && holdingDays ? parseInt(holdingDays, 10) : null;

    if (exitMode === 'holding') {
      if (!Number.isFinite(holdD) || holdD < 1) {
        alert('Enter a valid holding period (whole days, at least 1).');
        return;
      }
      if (!Number.isFinite(entryTh)) {
        alert('Please enter a valid entry threshold');
        return;
      }
    } else if (!Number.isFinite(entryTh) || !Number.isFinite(exitTh)) {
      alert('Please enter valid entry and exit threshold values');
      return;
    }

    const result = computeCustomStrategy({
      indicator,
      indicatorSeries,
      pricePoints,
      strategyType,
      entryThreshold: entryTh,
      exitThreshold: exitMode === 'holding' ? 0 : exitTh,
      entryOperator,
      exitOperator,
      entryCrossoverDir: strategyType === 'crossover' ? entryCrossoverDir : null,
      exitCrossoverDir: strategyType === 'crossover' ? exitCrossoverDir : null,
      maxDays: maxD,
      exitMode,
      holdingDays: exitMode === 'holding' ? holdD : null,
    });

    onResult(result);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Custom Strategy Backtest" width={600}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:16 }}>
        <div>
          <label style={{ display:"block", marginBottom:8, fontWeight:600, color: BRAND.text }}>
            Strategy Type
          </label>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            <label style={{ display:"flex", gap:6, alignItems:"center", cursor:"pointer", color: BRAND.text }}>
              <input
                type="radio"
                name="strategyType"
                value="dca"
                checked={strategyType === 'dca'}
                onChange={(e) => setStrategyType(e.target.value)}
              />
              <span>DCA (Dollar Cost Average)</span>
            </label>
            <label style={{ display:"flex", gap:6, alignItems:"center", cursor:"pointer", color: BRAND.text }}>
              <input
                type="radio"
                name="strategyType"
                value="crossover"
                checked={strategyType === 'crossover'}
                onChange={(e) => setStrategyType(e.target.value)}
              />
              <span>Crossover</span>
            </label>
            <label style={{ display:"flex", gap:6, alignItems:"center", cursor:"pointer", color: BRAND.text }}>
              <input
                type="radio"
                name="strategyType"
                value="single"
                checked={strategyType === 'single'}
                onChange={(e) => setStrategyType(e.target.value)}
              />
              <span>Single Entry/Exit</span>
            </label>
          </div>
        </div>

        <div>
          <label style={{ display:"block", marginBottom:8, fontWeight:600, color: BRAND.text }}>
            Entry Condition
          </label>
          {strategyType === 'crossover' ? (
            <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:8 }}>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <select
                  value={entryCrossoverDir}
                  onChange={(e) => setEntryCrossoverDir(e.target.value)}
                  style={{ background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
                >
                  <option value="up">Cross Up (from below)</option>
                  <option value="down">Cross Down (from above)</option>
                </select>
                <input
                  type="number"
                  step="any"
                  value={entryThreshold}
                  onChange={(e) => setEntryThreshold(e.target.value)}
                  placeholder="Threshold"
                  style={{ flex:1, background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
                />
                <span style={{ color: BRAND.muted, fontSize:12 }}>
                  {entryCrossoverDir === 'up' ? 'Buy on cross up' : 'Buy on cross down'}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <select
                value={entryOperator}
                onChange={(e) => setEntryOperator(e.target.value)}
                style={{ background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
              >
                <option value="<">&lt;</option>
                <option value="<=">&lt;=</option>
                <option value=">">&gt;</option>
                <option value=">=">&gt;=</option>
              </select>
              <input
                type="number"
                step="any"
                value={entryThreshold}
                onChange={(e) => setEntryThreshold(e.target.value)}
                placeholder="Threshold"
                style={{ flex:1, background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
              />
              <span style={{ color: BRAND.muted, fontSize:12 }}>
                {strategyType === 'dca' ? 'Buy while condition holds' : 'Buy when condition met'}
              </span>
            </div>
          )}
        </div>

        <div>
          <label style={{ display:"block", marginBottom:8, fontWeight:600, color: BRAND.text }}>
            Exit
          </label>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:12 }}>
            <label style={{ display:"flex", gap:6, alignItems:"center", cursor:"pointer", color: BRAND.text }}>
              <input
                type="radio"
                name="exitMode"
                checked={exitMode === 'indicator'}
                onChange={() => setExitMode('indicator')}
              />
              <span>Indicator condition</span>
            </label>
            <label style={{ display:"flex", gap:6, alignItems:"center", cursor:"pointer", color: BRAND.text }}>
              <input
                type="radio"
                name="exitMode"
                checked={exitMode === 'holding'}
                onChange={() => setExitMode('holding')}
              />
              <span>Fixed holding period (calendar days)</span>
            </label>
          </div>

          {exitMode === 'holding' ? (
            <div>
              <label style={{ display:"block", marginBottom:6, fontSize:13, color: BRAND.muted }}>
                Sell at the first price bar on or after entry plus this many days (DCA uses start of the entry window).
              </label>
              <input
                type="number"
                min="1"
                value={holdingDays}
                onChange={(e) => setHoldingDays(e.target.value)}
                placeholder="e.g. 50 or 100"
                style={{ width:"100%", maxWidth:280, background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
              />
            </div>
          ) : strategyType === 'crossover' ? (
            <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:8 }}>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <select
                  value={exitCrossoverDir}
                  onChange={(e) => setExitCrossoverDir(e.target.value)}
                  style={{ background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
                >
                  <option value="down">Cross Down (from above)</option>
                  <option value="up">Cross Up (from below)</option>
                </select>
                <input
                  type="number"
                  step="any"
                  value={exitThreshold}
                  onChange={(e) => setExitThreshold(e.target.value)}
                  placeholder="Threshold"
                  style={{ flex:1, background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
                />
                <span style={{ color: BRAND.muted, fontSize:12 }}>
                  {exitCrossoverDir === 'down' ? 'Sell on cross down' : 'Sell on cross up'}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <select
                value={exitOperator}
                onChange={(e) => setExitOperator(e.target.value)}
                style={{ background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
              >
                <option value="<">&lt;</option>
                <option value="<=">&lt;=</option>
                <option value=">">&gt;</option>
                <option value=">=">&gt;=</option>
              </select>
              <input
                type="number"
                step="any"
                value={exitThreshold}
                onChange={(e) => setExitThreshold(e.target.value)}
                placeholder="Threshold"
                style={{ flex:1, background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
              />
              <span style={{ color: BRAND.muted, fontSize:12 }}>
                Sell when condition met
              </span>
            </div>
          )}
        </div>

        {exitMode === 'indicator' && (
        <div>
          <label style={{ display:"block", marginBottom:8, fontWeight:600, color: BRAND.text }}>
            Max holding (optional cap)
          </label>
          <input
            type="number"
            min="1"
            value={maxDays}
            onChange={(e) => setMaxDays(e.target.value)}
            placeholder="Force exit after N days if indicator exit not hit"
            style={{ width:"100%", background: BRAND.bg, color: BRAND.text, border:`1px solid ${BRAND.border}`, borderRadius:8, padding:"4px 8px" }}
          />
          <div style={{ marginTop:8, fontSize:12, color: BRAND.muted, lineHeight:1.55 }}>
            Each entry cross is scored as its own trade. The cap only forces the exit date for that trade if the indicator exit has not fired yet; it does not remove or merge other entry crosses, so <strong>n</strong> stays tied to how many valid entry signals occurred in the history.
          </div>
        </div>
        )}

        <div style={{ padding:12, background: BRAND.panel, borderRadius:8, border:`1px solid ${BRAND.border}` }}>
          <div style={{ fontSize:12, color: BRAND.muted, lineHeight:1.6 }}>
            <strong>Strategy Types:</strong><br/>
            • <strong>DCA:</strong> Each time the entry condition turns on (leading edge), average price while it stays true, then exit by indicator or max-holding cap. Every episode is its own instance (overlaps allowed).<br/>
            • <strong>Crossover:</strong> Every entry cross is a separate hypothetical trade to its own exit (indicator cross or max-holding cap). Instances can overlap in time.<br/>
            • <strong>Single:</strong> Same leading-edge rule as DCA, but each instance uses a single entry price at the turn-on bar, then indicator or cap exit.<br/>
            • <strong>Fixed holding period:</strong> Exit uses the close (or bar) price on the first trading day on or after entry + N calendar days
          </div>
        </div>
      </div>
      <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end", gap:8 }}>
        <button style={btn} onClick={onClose}>Cancel</button>
        <button style={btnPrimary} onClick={handleRun}>Run Backtest</button>
      </div>
    </Modal>
  );
}

/* -----------------------------
   Component
--------------------------------*/
export default function StockChart({
  ticker,
  mode = "indicators", // 'fundamentals' | 'indicators'
  indModal: propIndModal,
  setIndModal: propSetIndModal,
  metModal: propMetModal,
  setMetModal: propSetMetModal,
  customStrategyModal: propCustomStrategyModal,
  setCustomStrategyModal: propSetCustomStrategyModal,
}) {
  const symbol = (ticker || "AAPL").toUpperCase();
  const isFundamentals = mode === "fundamentals";
  const isIndicators = mode === "indicators";
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // UI state - use props if provided, otherwise use local state
  const [localIndModal, setLocalIndModal] = useState(false);
  const [localMetModal, setLocalMetModal] = useState(false);
  const [localCustomStrategyModal, setLocalCustomStrategyModal] = useState(false);
  
  const indModal = propIndModal !== undefined ? propIndModal : localIndModal;
  const setIndModal = propSetIndModal || setLocalIndModal;
  const metModal = propMetModal !== undefined ? propMetModal : localMetModal;
  const setMetModal = propSetMetModal || setLocalMetModal;
  const customStrategyModal = propCustomStrategyModal !== undefined ? propCustomStrategyModal : localCustomStrategyModal;
  const setCustomStrategyModal = propSetCustomStrategyModal || setLocalCustomStrategyModal;
  
  const [customStrategyResult, setCustomStrategyResult] = useState(null);
  const chartRef = React.useRef(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCompanyLogoUrl(null);
    getCompanyLogo(symbol).then((url) => {
      if (!cancelled) setCompanyLogoUrl(url);
    });
    return () => { cancelled = true; };
  }, [symbol]);

  const [metricKey, setMetricKey] = useState(() => "none");
  const [metricDisplayType, setMetricDisplayType] = useState("line"); // 'line' | 'column'
  const [metricPeriod, setMetricPeriod] = useState(METRIC_PERIOD_QUARTER); // 'quarter' | 'year'
  const [indicator, setIndicator] = useState("rmo");
  const [zScoreYearsMap, setZScoreYearsMap] = useState(() => loadZScoreYearsMap());
  const [zScoreModal, setZScoreModal] = useState({ open: false, indicator: null });
  const [comboMembers, setComboMembers] = useState(() => loadComboMembers());
  const [comboModal, setComboModal] = useState(false);
  const [interval, setInterval]   = useState("daily");
  const [isLog, setIsLog]         = useState(false);

  // Bands on price
  const [stdWindow, setStdWindow] = useState(50);
  const [maxSigma, setMaxSigma]   = useState(5);
  const [showBands, setShowBands] = useState(false);

  // Data
  const [priceSeries, setPriceSeries]   = useState([]);   // [ms,value]
  const [bottomSeries, setBottomSeries] = useState([]);
  const [realizedPriceOverlaySeries, setRealizedPriceOverlaySeries] = useState([]);
  const [bottomSeriesAlt, setBottomSeriesAlt] = useState([]);
  const [bottomSeriesFwd, setBottomSeriesFwd] = useState([]);

  const [priceForecastEps, setPriceForecastEps] = useState(null);
  const [priceForecastRev, setPriceForecastRev] = useState(null);
  const [priceForecastChartEps, setPriceForecastChartEps] = useState(null);
  const [priceForecastChartRev, setPriceForecastChartRev] = useState(null);
  const [correlationsByMetric, setCorrelationsByMetric] = useState([]);
  const [correlationsLoading, setCorrelationsLoading] = useState(false);
  const [correlationSortBy, setCorrelationSortBy] = useState("all");
  const [revValBounds, setRevValBounds] = useState([]);
  const [epsValBounds, setEpsValBounds] = useState([]);
  const [indicatorCalc, setIndicatorCalc] = useState(null);
  /** Fetched payload only; `fundamentalsData` merges this with `buildSeriesCache` in useMemo. */
  const [chartFetchBundle, setChartFetchBundle] = useState(null);

  const lagDays = 0;

  const chartIndicator = normalizeZScoreIndicatorKey(indicator);
  const activeZScoreYears = getZScoreYearsForIndicator(indicator, zScoreYearsMap);
  const comboExtra = { comboMembers, zScoreYearsMap };

  useEffect(() => {
    if (isLegacyZScore5yKey(indicator)) {
      const base = normalizeZScoreIndicatorKey(indicator);
      setIndicator(base);
      setZScoreYearsMap((m) => saveZScoreYearsForIndicator(base, 5, m));
    }
  }, []);

  const handlePickIndicator = (k) => {
    if (isClassicNovelCombo(k)) {
      setComboModal(true);
      return;
    }
    if (isZScoreConfigurable(k)) {
      setZScoreModal({ open: true, indicator: k });
      return;
    }
    setIndicator(k);
  };

  const handleZScoreYearsConfirm = (base, years) => {
    setZScoreYearsMap((m) => saveZScoreYearsForIndicator(base, years, m));
    setIndicator(base);
    setZScoreModal({ open: false, indicator: null });
    setIndModal(false);
  };

  const handleComboMembersConfirm = (members) => {
    const next = saveComboMembers(members);
    setComboMembers(next);
    setIndicator(CLASSIC_NOVEL_COMBO_KEY);
    setComboModal(false);
    setIndModal(false);
  };

  const indicatorSeriesCache = useMemo(() => {
    if (!chartFetchBundle) return null;
    // Fundamentals tab never renders indicators — skip the full indicator cache build.
    if (mode === "fundamentals") return {};
    const cacheFilter = chartIndicator;
    try {
      return buildSeriesCache(
        chartFetchBundle.fundamentals,
        chartFetchBundle.pxForMath,
        interval,
        lagDays,
        cacheFilter,
        {
          zScoreYears: activeZScoreYears,
          comboMembers: isClassicNovelCombo(chartIndicator) ? comboMembers : undefined,
        }
      );
    } catch (e) {
      if (IS_DEV) console.error("[StockChart] buildSeriesCache failed:", e);
      return null;
    }
  }, [chartFetchBundle, mode, chartIndicator, interval, lagDays, activeZScoreYears, comboMembers]);

  const fundamentalsData = useMemo(() => {
    if (!chartFetchBundle || indicatorSeriesCache === null) return null;
    try {
      const { mPts, estPts, dcfPts } = computeMetricOverlayPoints({
        fundamentals: chartFetchBundle.fundamentals,
        metricKey,
        metricPeriod,
        pxSec: chartFetchBundle.pxForMath,
        interval,
        lagDays,
        dcfRes: chartFetchBundle.dcfRes,
        aaaYieldPoints: chartFetchBundle.aaaYieldPoints,
        aaaFallbackPct: chartFetchBundle.aaaFallbackPct,
      });
      return {
        ...chartFetchBundle,
        seriesCache: indicatorSeriesCache,
        mPts,
        estPts,
        dcfPts,
        metricSeriesChart: (mPts || []).map(p => [p.time * 1000, p.value]),
        estSeriesChart: (estPts || []).map(p => [p.time * 1000, p.value]),
        dcfSeriesChart: (dcfPts || []).map(p => [p.time * 1000, p.value]),
      };
    } catch (e) {
      if (IS_DEV) console.error("[StockChart] fundamentalsData merge failed:", e);
      return null;
    }
  }, [chartFetchBundle, indicatorSeriesCache, metricKey, metricPeriod, interval, lagDays]);

  const metricSeries = fundamentalsData?.metricSeriesChart ?? [];
  const estSeries = fundamentalsData?.estSeriesChart ?? [];
  const dcfSeries = fundamentalsData?.dcfSeriesChart ?? [];

  // New ticker: clear fundamental overlays immediately. Otherwise Graham/DCF from the prior symbol
  // can linger—dates often overlap the next stock’s range, so “clip to price” still shows wrong values.
  useEffect(() => {
    setChartFetchBundle(null);
  }, [symbol]);

  useEffect(() => {
    if (mode === "indicators") setMetricKey("none");
  }, [mode]);

  // If we skipped the heavy volume request (e.g. default RMO), fetch it when user picks an indicator that needs OHLCV volume.
  useEffect(() => {
    if (mode !== "indicators") return;
    if (!VOLUME_BAR_INDICATORS.includes(indicator)) return;
    if (!chartFetchBundle?.fundamentals) return;
    const bars = chartFetchBundle.fundamentals._dailyBars;
    const hasVol =
      Array.isArray(bars) &&
      bars.length > 0 &&
      bars.some((b) => Number.isFinite(b?.volume) && b.volume > 0);
    if (hasVol) return;

    let cancelled = false;
    (async () => {
      try {
        const vol = await getPrice(symbol, { withVolume: true, timeseries: 7500 }).catch(() => []);
        if (cancelled || !Array.isArray(vol) || !vol.length) return;
        setChartFetchBundle((prev) => {
          if (!prev?.fundamentals) return prev;
          return {
            ...prev,
            fundamentals: { ...prev.fundamentals, _dailyBars: vol },
          };
        });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol, indicator, mode, chartFetchBundle]);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      try {
        const isIndicatorsTab = mode === "indicators";
        const isFundamentalsTab = mode === "fundamentals";
        const indicatorNeedsForecasts =
          indicator === "pe_peratio" || indicator === "forward_pe" || indicator === "rev_val" || indicator === "eps_val" || indicator === "fgpz" || indicator === "gag";
        const metricNeedsForecasts =
          metricKey === "eps_estimate" || metricKey === "revenue_estimate" || metricKey === "revenue_yoy_forecast";
        const needDcf = metricKey === "dcf";
        const needScores = false;
        const needConsensus = false;
        const needAaa = metricKey === "graham_intrinsic";
        const needAnalystForecasts =
          isFundamentalsTab || metricNeedsForecasts || indicatorNeedsForecasts;

        const needVolumeBars =
          !isIndicatorsTab ||
          VOLUME_BAR_INDICATORS.includes(indicator);

        if (IS_DEV) console.log(`[StockChart] Fetching data for ${symbol}`);
        const [priceRes, priceVolRes, fundRes, dcfRes, scoresRes, analystConsensusRes, aaaRes] = await Promise.all([
          getPrice(symbol, { withVolume: false, timeseries: isFundamentalsTab ? 7500 : 20000 }),
          needVolumeBars
            ? getPrice(symbol, { withVolume: true, timeseries: 7500 }).catch(() => [])
            : Promise.resolve([]),
          getFundamentals(symbol),
          needDcf ? getDCFHistory(symbol, 5, 0.1) : Promise.resolve([]),
          needScores ? getScoresSeries(symbol) : Promise.resolve({}),
          needConsensus ? getAnalystConsensus(symbol).catch(() => []) : Promise.resolve([]),
          needAaa ? getAaaCorporateYield().catch(() => ({ points: [], fallbackPct: 5 })) : Promise.resolve({ points: [], fallbackPct: 5 }),
        ]);
        if (cancelled) return;

        const aaaParsed = parseAaaYieldApiResponse(aaaRes);

        if (IS_DEV) {
          console.log(`[StockChart] Received price data:`, priceRes?.length || 0, "points");
          console.log(`[StockChart] Fundamentals:`, fundRes ? "received" : "missing");
        }

        const pxRaw = priceRes || [];
        const pxVolRaw =
          needVolumeBars && Array.isArray(priceVolRes) && priceVolRes.length ? priceVolRes : pxRaw;
        const pxSec = aggregateToInterval(pxRaw, interval);
        const realPx = pxSec.map(p => [p.time * 1000, p.value]);
        setPriceSeries(realPx);

        const fundamentals = fundRes || {};
        const {
          income = [],
          cashflow = [],
          metrics = [],
          earnings = [],
          balanceSheet = [],
          annualIncome = [],
        } = fundamentals;

        // Novel technicals (MRVZ/VDDM/NUPL) require volume; feed volume-enabled bars.
        fundamentals._dailyBars = pxVolRaw;
        const pxForMath = pxSec;
        
        if (needAnalystForecasts) {
          try {
            const analystForecastsData = await getAnalystForecasts(
              symbol,
              fundamentals.analystEstimates,
              fundamentals
            );
            fundamentals.analystForecasts = analystForecastsData;
          } catch (err) {
            console.warn(`[StockChart] Failed to fetch analyst forecasts for ${symbol}:`, err);
            fundamentals.analystForecasts = buildAnalystForecastsFromFundamentals(symbol, fundamentals);
          }
        } else {
          fundamentals.analystForecasts = null;
        }

        // Raw bundle; overlay metric series derived in useMemo (no refetch when only metricKey changes).
        setChartFetchBundle({
          fundamentals: {
            ...fundamentals,
            ...(needScores && scoresRes && typeof scoresRes === "object" ? scoresRes : {}),
            analystConsensusRes: needConsensus ? analystConsensusRes : [],
          },
          pxForMath,
          pxSec,
          dcfRes,
          aaaYieldPoints: aaaParsed.points,
          aaaFallbackPct: aaaParsed.fallbackPct,
        });

        // Graham correlation needs AAA yield; fetch in background so initial paint stays fast.
        if (isFundamentalsTab && !needAaa) {
          scheduleIdleTask(async () => {
            if (cancelled) return;
            try {
              const aaaBg = await getAaaCorporateYield().catch(() => ({ points: [], fallbackPct: 5 }));
              if (cancelled) return;
              const parsed = parseAaaYieldApiResponse(aaaBg);
              setChartFetchBundle((prev) => {
                if (!prev || prev.aaaYieldPoints?.length) return prev;
                return {
                  ...prev,
                  aaaYieldPoints: parsed.points,
                  aaaFallbackPct: parsed.fallbackPct,
                };
              });
            } catch {
              /* ignore */
            }
          }, 800);
        }

      } catch (e) {
        console.error(`[StockChart] Error fetching data for ${symbol}:`, e);
        if (IS_DEV) {
          console.error(`[StockChart] Error details:`, {
            message: e.message,
            response: e.response?.data,
            status: e.response?.status,
            url: e.config?.url,
          });
        }
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [symbol, interval, lagDays, mode, indicator, mode === "indicators" ? metricKey : null]);

  // Lazy-load DCF / AAA when user picks those metrics on the Fundamentals tab.
  useEffect(() => {
    if (mode !== "fundamentals" || !chartFetchBundle) return;
    const needsDcf = metricKey === "dcf";
    const needsAaa = metricKey === "graham_intrinsic";
    if (!needsDcf && !needsAaa) return;

    const hasDcf = Array.isArray(chartFetchBundle.dcfRes) && chartFetchBundle.dcfRes.length > 0;
    const hasAaa = Array.isArray(chartFetchBundle.aaaYieldPoints) && chartFetchBundle.aaaYieldPoints.length > 0;
    const fetchDcf = needsDcf && !hasDcf;
    const fetchAaa = needsAaa && !hasAaa;
    if (!fetchDcf && !fetchAaa) return;

    let cancelled = false;
    (async () => {
      const updates = {};
      if (fetchDcf) {
        updates.dcfRes = await getDCFHistory(symbol, 5, 0.1).catch(() => []);
      }
      if (fetchAaa) {
        const aaaRes = await getAaaCorporateYield().catch(() => ({ points: [], fallbackPct: 5 }));
        const parsed = parseAaaYieldApiResponse(aaaRes);
        updates.aaaYieldPoints = parsed.points;
        updates.aaaFallbackPct = parsed.fallbackPct;
      }
      if (cancelled || !Object.keys(updates).length) return;
      setChartFetchBundle((prev) => (prev ? { ...prev, ...updates } : prev));
    })();
    return () => { cancelled = true; };
  }, [symbol, mode, metricKey, chartFetchBundle]);

  // EPS/Revenue/YoY forecast modes: extend the price series into the future with invisible
  // placeholder points so the stock ordinal axis keeps calendar-proportional spacing.
  const pricePlaceholderSeries = useMemo(() => {
    if (
      metricKey !== "eps_estimate" &&
      metricKey !== "revenue_estimate" &&
      metricKey !== "revenue_yoy_forecast"
    ) {
      return [];
    }
    if (!priceSeries?.length) return [];
    const last = priceSeries[priceSeries.length - 1];
    const dayMs = 24 * 60 * 60 * 1000;
    const placeholder = [[last[0], last[1]]];
    const isAnnual = metricPeriod === METRIC_PERIOD_YEAR;
    const periodsForward = isAnnual ? 3 : FORWARD_ESTIMATE_QUARTERS;
    if (interval === "monthly") {
      const d = new Date(last[0]);
      const months = isAnnual ? periodsForward * 12 : periodsForward * 3;
      for (let i = 1; i <= months; i++) {
        d.setUTCMonth(d.getUTCMonth() + 1);
        placeholder.push([d.getTime(), last[1]]);
      }
    } else {
      const stepMs = interval === "weekly" ? 7 * dayMs : dayMs;
      const steps = isAnnual
        ? (interval === "weekly" ? periodsForward * 52 : periodsForward * 365)
        : (interval === "weekly" ? periodsForward * 13 : periodsForward * 91);
      for (let i = 1; i <= steps; i++) {
        placeholder.push([last[0] + i * stepMs, last[1]]);
      }
    }
    return placeholder;
  }, [metricKey, metricPeriod, interval, priceSeries]);

  useEffect(() => {
    if (!fundamentalsData) {
      setCorrelationsLoading(false);
      setCorrelationsByMetric([]);
      return;
    }
    const { fundamentals, pxForMath, seriesCache, mPts, pxSec, dcfRes, aaaYieldPoints, aaaFallbackPct } = fundamentalsData;

    // Extract income, cashflow, and balanceSheet from fundamentals
    const income = fundamentals.income || [];
    const cashflow = fundamentals.cashflow || [];
    const balanceSheet = fundamentals.balanceSheet || [];

    // Recreate helper functions needed here
    const minPxTime = pxForMath?.length ? pxForMath[0].time : Number.POSITIVE_INFINITY;
    const cropToPrice = (arr = []) =>
      (arr || []).filter(p => p && Number.isFinite(p.time) && p.time >= minPxTime);

    function shiftAndMap(arr, valueSelector) {
      return cropToPrice(
        (arr || [])
          .map(e => {
            const d = new Date(e.fillingDate || e.acceptedDate || e.date);
            d.setDate(d.getDate() + lagDays);
            const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
            const [Y, M, D] = key.split("-").map(Number);
            const time = Date.UTC(Y, M - 1, D) / 1000;
            const val = valueSelector(e);
            return (val != null && isFinite(val)) ? { time, value: val } : null;
          })
          .filter(Boolean)
          .sort((a,b)=>a.time-b.time)
      );
    }
    function enforceStrictAsc(arr = []) {
      const m = new Map();
      for (const p of arr) {
        if (p && Number.isFinite(p.time) && Number.isFinite(p.value)) m.set(p.time, p.value);
      }
      return Array.from(m.entries()).sort((a,b)=>a[0]-b[0]).map(([time,value])=>({ time, value }));
    }

    let cancelled = false;

    if (isIndicators) {
    // bottom indicators
    const divPts = buildDivergence(mPts, pxForMath);
    const rsiPts = computeFundDivRSI(divPts);
    let peDevPts = [];
    if (indicator === "pe_dev") {
      const epsPts = seriesCache.eps || [];
      const FIVE_Y = 5 * 365 * 24 * 3600;
      const win = [];
      peDevPts = pxForMath.map(bar => {
        let i = -1;
        for (let j = epsPts.length - 1; j >= 0; j--) {
          if (epsPts[j].time <= bar.time) { i = j; break; }
        }
        const eps = (i >= 0) ? epsPts[i].value : null;
        if (!(eps != null && isFinite(eps) && eps > 0)) return null;
        const pe = bar.value / eps;
        if (!isFinite(pe)) return null;
        win.push({ time: bar.time, pe });
        while (win.length && (bar.time - win[0].time) > FIVE_Y) win.shift();
        const vals = win.map(x => x.pe);
        const ref  = median(vals);
        if (!(ref != null && isFinite(ref) && ref > 0)) return null;
        const devPct = clamp((pe / ref - 1) * 100, -100, 100);
        return { time: bar.time, value: devPct };
      }).filter(Boolean);
    }

    const fScoreServer = (fundamentals.f || []).map(r => ({ time: Math.floor(new Date(r.date).getTime()/1000), value: r.value }));
    const zScoreServer = (fundamentals.z || []).map(r => ({ time: Math.floor(new Date(r.date).getTime()/1000), value: r.value }));

    const fScoreLocal = buildFScoreTTMSeries(
      income, cashflow, balanceSheet, interval, lagDays
    );

    function lastTs(arr){ return arr?.length ? arr[arr.length-1].time : -Infinity; }
    const lastServer = lastTs(fScoreServer);
    const lastLocal  = lastTs(fScoreLocal);
    let fScoreSeries;
    if (lastLocal > lastServer)      fScoreSeries = fScoreLocal;
    else if (lastServer > lastLocal) fScoreSeries = fScoreServer;
    else {
      const merged = new Map();
      fScoreServer.forEach(p => merged.set(p.time, p.value));
      fScoreLocal.forEach(p => merged.set(p.time, p.value));
      fScoreSeries = Array.from(merged.entries())
        .map(([time,value])=>({ time, value }))
        .sort((a,b)=>a.time-b.time);
    }

    const zScoreSeries = zScoreServer.length ? zScoreServer : (seriesCache.altmanZ || []);
    const pbPts         = seriesCache.pb;
    const pegPts        = seriesCache.peg;
    const evYieldPts = seriesCache.evYield || [];
    const syoPts        = seriesCache.syo || [];
    const rmoPts        = seriesCache.rmo || [];
    const tamoPts       = seriesCache.tamo || [];
    const nimoPts       = seriesCache.nimo || [];
    const cfaoPts       = seriesCache.cfao || [];
    const cfaoDeltaPts  = seriesCache.cfao_delta || [];
    const vmzPts        = seriesCache.vmz || [];
    const ccePts        = seriesCache.cce || [];
    const oeyPts        = seriesCache.oey || [];
    const vmoPts        = seriesCache.vmo || [];
    const revSurprisePts = seriesCache.revSurprise || [];
    const edPts         = seriesCache.ed  || [];
    
    // Process analyst consensus series (convert date strings to epoch timestamps)
    const analystConsensusPts = (fundamentals.analystConsensusRes || []).map(r => {
      if (!r?.date || r.value == null) return null;
      const [Y, M, D] = r.date.split("-").map(Number);
      if (!Number.isFinite(Y) || !Number.isFinite(M) || !Number.isFinite(D)) return null;
      return {
        time: Math.floor(Date.UTC(Y, M - 1, D) / 1000),
        value: Number(r.value)
      };
    }).filter(Boolean).sort((a, b) => a.time - b.time);

    const zSeries = (() => {
      if (!pxForMath?.length) return [];
      const rb = rollingBands(pxForMath, stdWindow, maxSigma);
      return pxForMath.map((p, i) => {
        const sd = rb.sd[i]?.value || 0;
        const mu = rb.mean[i]?.value || 0;
        return { time:p.time, value: sd>0 ? (p.value - mu)/sd : 0 };
      });
    })();

    const pickBottom = () => {
      if (indicator === "mean_rev") return zSeries;
      if (isClassicNovelCombo(chartIndicator)) {
        return getSeriesForIndicatorKey(seriesCache, CLASSIC_NOVEL_COMBO_KEY, activeZScoreYears, comboExtra);
      }
      if (isZScoreConfigurable(chartIndicator)) {
        return getSeriesForIndicatorKey(seriesCache, chartIndicator, activeZScoreYears);
      }
      switch (indicator) {
        case "pe_dev":    return peDevPts;
        case "ev_ebitda":
          return evYieldPts;
        case "div_yield": return seriesCache.divYield || [];
        case "peg_ratio": return seriesCache.pegQ || [];
        case "fgpz": return seriesCache.fgpz || [];
        case "gag": return seriesCache.gag || [];
        case "syo":       return syoPts;
        case "rmo":       return rmoPts;
        case "tamo":      return tamoPts;
        case "nimo":      return nimoPts;
        case "vmz":       return vmzPts;
        case "mrvz":      return seriesCache.mrvz || [];
        case "oey":       return oeyPts;
        case "vmo":       return vmoPts;
        case "ed":        return edPts;
        case "vddm":      return seriesCache.vddm || [];
        case "nupl":      return seriesCache.nupl || [];
        case "realized_price_252": return seriesCache.realizedPrice252 || [];
        case "realized_price_504": return seriesCache.realizedPrice504 || [];
        case "realized_price_5y": return seriesCache.realizedPrice5y || [];
        case "risk":      return seriesCache.risk || [];
        case "srm":       return seriesCache.srm || [];
        case "pe_peratio": return seriesCache.pePERatio || [];
        case "forward_pe": return seriesCache.forwardPe || [];
        case "ttm_pe": return seriesCache.ttmPe || [];
        case "revenue_cagr": return seriesCache.revenueCagr2y || seriesCache.revenueCagr || [];
        case "eps_cagr": return seriesCache.epsCagr2y || seriesCache.epsCagr || [];
        case "rev_val": return (seriesCache.revVal?.series || []).map(p => ({ time: p.time, value: p.value }));
        case "eps_val": return (seriesCache.epsVal?.series || []).map(p => ({ time: p.time, value: p.value }));
        default:          return [];
      }
    };

    const toChartPts = (arr) => (arr || [])
      .filter(p => p && Number.isFinite(p.time) && Number.isFinite(p.value))
      .sort((a, b) => a.time - b.time)
      .map(p => [p.time * 1000, p.value]);

    const rawBottom = pickBottom();
    if (isRealizedPriceIndicator(indicator)) {
      setRealizedPriceOverlaySeries(toChartPts(rawBottom));
      setBottomSeries(toChartPts(computeRealizedPriceDerivative(rawBottom)));
    } else {
      setRealizedPriceOverlaySeries([]);
      setBottomSeries(toChartPts(rawBottom));
    }

    if (indicator === "revenue_cagr") {
      setBottomSeriesAlt(toChartPts(seriesCache.revenueCagr1y || []));
      setBottomSeriesFwd(toChartPts(seriesCache.revenueCagrFwd || []));
    } else if (indicator === "eps_cagr") {
      setBottomSeriesAlt(toChartPts(seriesCache.epsCagr1y || []));
      setBottomSeriesFwd(toChartPts(seriesCache.epsCagrFwd || []));
    } else {
      setBottomSeriesAlt([]);
      setBottomSeriesFwd([]);
    }

    setRevValBounds(seriesCache.revVal?.bounds || []);
    setEpsValBounds(seriesCache.epsVal?.bounds || []);

    const zScoreSeriesForCalc = isClassicNovelCombo(chartIndicator)
      ? getSeriesForIndicatorKey(seriesCache, CLASSIC_NOVEL_COMBO_KEY, activeZScoreYears, comboExtra)
      : isZScoreConfigurable(chartIndicator)
      ? getSeriesForIndicatorKey(seriesCache, chartIndicator, activeZScoreYears)
      : undefined;

    // indicator calcs table
    const incPts = shiftAndMap(income, e => e.netIncome ?? 0).map(p => ({ time:p.time }));
    const calc = computeIndicatorCalculations({
      indicator: chartIndicator,
      zScoreYears: activeZScoreYears,
      zScoreSeries: zScoreSeriesForCalc,
      pricePoints: pxForMath,
      srmSeries: seriesCache.srm,
      incomePts: incPts,
      rsiSeries: computeFundDivRSI(buildDivergence(mPts, pxForMath)),
      fSeries: fScoreSeries,
      evYieldSeries: evYieldPts,
      divYieldZ2ySeries: seriesCache.divYieldZ2y || [],
      divYieldZ5ySeries: seriesCache.divYieldZ5y || [],
      pbSeries: seriesCache.pb,
      pbNormalizedSeries: seriesCache.pbNormalized || [],
      pbNormalized5ySeries: seriesCache.pbNormalized5y || [],
      peNormalizedSeries: seriesCache.peNormalized || [],
      peNormalized5ySeries: seriesCache.peNormalized5y || [],
      poiNormalizedSeries: seriesCache.poiNormalized || [],
      poiNormalized5ySeries: seriesCache.poiNormalized5y || [],
      pegSeries: pegPts,
      pegQuantSeries: seriesCache.pegQ,
      syoSeries: syoPts,
      stdWindow,
      maxSigma,
      vmoSeries: vmoPts,
      mrvzSeries: seriesCache.mrvz || [],
      rmoSeries: rmoPts,
      tamoSeries: tamoPts,
      nimoSeries: nimoPts,
      cfaoSeries: cfaoPts,
      cfaoDeltaSeries: cfaoDeltaPts,
      vmzSeries: vmzPts,
      cceSeries: ccePts,
      oeySeries: oeyPts,
      revSurpriseSeries: revSurprisePts,
      vddmSeries: seriesCache.vddm || [],
      edSeries: edPts,
      nuplSeries: seriesCache.nupl || [],
      realizedPriceSeries: isRealizedPriceIndicator(indicator) ? rawBottom : [],
      riskSeries: seriesCache.risk || [],
      peDevSeries: peDevPts,
      psNormalizedSeries: seriesCache.psNormalized || [],
      psNormalized5ySeries: seriesCache.psNormalized5y || [],
      cfPriceNormalizedSeries: seriesCache.cfPriceNormalized || [],
      cfPriceNormalized5ySeries: seriesCache.cfPriceNormalized5y || [],
      pePERatioSeries: seriesCache.pePERatio || [],
      revValSeries: seriesCache.revVal?.series || [],
      epsValSeries: seriesCache.epsVal?.series || [],
      fgpzSeries: seriesCache.fgpz || [],
      gagSeries: seriesCache.gag || [],
    });
    setIndicatorCalc(calc);
    } else {
      setBottomSeries([]);
      setBottomSeriesAlt([]);
      setBottomSeriesFwd([]);
      setRevValBounds([]);
      setEpsValBounds([]);
      setIndicatorCalc(null);
    }

    if (isFundamentals) {
      setCorrelationsLoading(true);
      function buildMetricPointsForKey(mk) {
        const opt = METRIC_OPTIONS[mk];
        if (!opt || opt.source === "none") return [];
        if (mk === "revenue_yoy") {
          const pts = buildRevenueYoYPoints(fundamentals.income || [], interval, lagDays, minPxTime);
          return cropToPrice(enforceStrictAsc(pts));
        }
        if (mk === "revenue_yoy_forecast") {
          return cropToPrice(
            enforceStrictAsc(
              buildRevenueYoYPoints(fundamentals.income || [], interval, lagDays, minPxTime)
            )
          );
        }
        if (mk === "eps" || mk === "revenue") {
          const isRevenue = mk === "revenue";
          const src = (fundamentals.earnings || fundamentals.earningsHistory || []);
          const pts = [];
          src.forEach(e => {
            const hasVal = isRevenue ? (e.revenue != null && isFinite(e.revenue)) : (e.eps != null && isFinite(e.eps));
            if (!hasVal || !(e.fillingDate || e.acceptedDate || e.date)) return;
            const d = new Date(e.fillingDate || e.acceptedDate || e.date);
            d.setDate(d.getDate() + lagDays);
            const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
            const [Y, M, D] = key.split("-").map(Number);
            const t = Date.UTC(Y, M - 1, D) / 1000;
            const val = isRevenue ? (e.revenue || 0) / 1e9 : (e.eps || 0);
            pts.push({ time: t, value: val });
          });
          return cropToPrice(enforceStrictAsc(pts));
        }
        if (opt.source === "combined") {
          // Correlate using income-statement actuals (same as chart metric line).
          const isRevenue = mk === "revenue_estimate";
          const raw = fundamentals.income || [];
          return shiftAndMap(raw, (e) => {
            if (isRevenue) {
              return e.revenue != null && isFinite(e.revenue) ? e.revenue / 1e9 : undefined;
            }
            return e.eps != null && isFinite(e.eps) ? e.eps : undefined;
          });
        }
        if (opt.source === "graham") {
          return cropToPrice(
            buildGrahamIntrinsicPoints(fundamentals, {
              interval,
              lagDays,
              minPxTime,
              maxPxTime: pxForMath?.length ? pxForMath[pxForMath.length - 1].time : null,
              aaaYieldPoints: aaaYieldPoints || [],
              fallbackYieldPercent: aaaFallbackPct ?? 5,
            })
          );
        }
        if (opt.source === "dcf") {
          return cropToPrice(
            (dcfRes || [])
              .map(e => {
                const d = new Date(e.fillingDate || e.acceptedDate || e.date);
                d.setDate(d.getDate() + lagDays);
                const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
                const [Y, M, D] = key.split("-").map(Number);
                const t = Date.UTC(Y, M - 1, D) / 1000;
                const v = e.dcfPerShare ?? e.dcf;
                return (v != null) ? { time: t, value: v } : null;
              })
              .filter(Boolean)
              .sort((a, b) => a.time - b.time)
          );
        }
        const raw = resolveMetricStatementRows(fundamentals, opt.source, metricPeriod);
        return shiftAndMap(raw, e => {
          if (opt.source === "balanceSheet") return balanceSheetMetricValue(e, opt.field);
          const rawVal = e[opt.field] ?? (opt.fieldAlt ? e[opt.fieldAlt] : undefined);
          return scaleFundamentalFieldValue(opt.field, rawVal);
        });
      }
      const lastN = (mPtsArr, yrs) => {
        const cutoff = pxForMath.length ? pxForMath[pxForMath.length - 1].time - yrs * 365 * 24 * 3600 : 0;
        const mp = mPtsArr.filter(p => p.time >= cutoff);
        const pp = pxForMath.filter(p => p.time >= cutoff);
        return (mp.length >= 2 && pp.length >= 2) ? calculateCorrelation(mp, pp) : null;
      };
      const metricKeys = Object.keys(METRIC_OPTIONS).filter((k) => k !== "none" && k !== "dcf");
      const results = [];
      let mi = 0;

      const runPriceForecastWork = () => {
        if (cancelled) return;
        const af = fundamentals.analystForecasts;
        const epsForward = forwardEstimateRows(af?.eps);
        const revForward = forwardEstimateRows(af?.revenue);
        if (af && pxForMath?.length && (epsForward.length || revForward.length)) {
          const findPxAtOrBefore = (t) => {
            const arr = pxForMath;
            let lo = 0;
            let hi = arr.length - 1;
            let best = null;
            while (lo <= hi) {
              const mid = (lo + hi) >> 1;
              if (arr[mid].time <= t) {
                best = arr[mid].value;
                lo = mid + 1;
              } else {
                hi = mid - 1;
              }
            }
            return best;
          };
          const src = (fundamentals.earnings || fundamentals.earningsHistory || []);
          const rows = [];
          src.forEach((e) => {
            const hasEpsEst = e.epsEstimated != null && isFinite(e.epsEstimated) && e.epsEstimated > 0;
            const hasRevEst = e.revenueEstimated != null && isFinite(e.revenueEstimated) && e.revenueEstimated > 0;
            if ((!hasEpsEst && !hasRevEst) || !(e.fillingDate || e.acceptedDate || e.date)) return;
            const d = new Date(e.fillingDate || e.acceptedDate || e.date);
            d.setDate(d.getDate() + lagDays);
            const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
            const [Y, M, D] = key.split("-").map(Number);
            const t = Date.UTC(Y, M - 1, D) / 1000;
            if (t < minPxTime) return;
            const price = findPxAtOrBefore(t);
            if (price == null || !(price > 0)) return;
            const epsEst = hasEpsEst ? e.epsEstimated : null;
            const revEstB = hasRevEst ? (e.revenueEstimated || 0) / 1e9 : null;
            rows.push({ time: t, epsEst, revEstB, price });
          });
          const byTime = new Map();
          rows.forEach((r) => byTime.set(r.time, r));
          const merged = Array.from(byTime.entries()).sort((a, b) => a[0] - b[0]).map(([, r]) => r);
          const epsOnly = merged.filter((r) => r.epsEst != null).map((r) => [r.epsEst, r.price]);
          const revOnly = merged.filter((r) => r.revEstB != null && r.revEstB > 0).map((r) => [r.revEstB, r.price]);
          const epsFit = epsOnly.length >= 6 ? fitLinearRegressionWithPI(epsOnly) : null;
          const revFit = revOnly.length >= 6 ? fitLinearRegressionWithPI(revOnly) : null;
          const resultEps = [];
          const resultRev = [];
          const futureEpsX = [];
          const futureRevX = [];
          for (let i = 0; i < FORWARD_ESTIMATE_QUARTERS; i++) {
            const epsQ = epsForward[i];
            const revQ = revForward[i];
            const date = epsQ?.date || revQ?.date || null;
            const epsEst = epsQ?.estimate != null && isFinite(epsQ.estimate) ? epsQ.estimate : null;
            const revEst = revQ?.estimate != null && isFinite(revQ.estimate) ? revQ.estimate / 1e9 : null;
            if (epsEst != null && epsEst > 0) futureEpsX.push(epsEst);
            if (revEst != null && revEst > 0) futureRevX.push(revEst);
            if (epsFit && epsEst != null && epsEst > 0) {
              const pred = epsFit.predict(epsEst);
              if (pred && pred.point > 0) {
                resultEps.push({
                  date,
                  point: pred.point,
                  low: Math.max(0.01, pred.low),
                  high: pred.high,
                });
              }
            }
            if (revFit && revEst != null && revEst > 0) {
              const pred = revFit.predict(revEst);
              if (pred && pred.point > 0) {
                resultRev.push({
                  date,
                  point: pred.point,
                  low: Math.max(0.01, pred.low),
                  high: pred.high,
                });
              }
            }
          }
          const chartEps = epsFit
            ? {
                historical: epsOnly,
                future: futureEpsX.map((x) => {
                  const pred = epsFit.predict(x);
                  return pred?.point > 0 ? [x, pred.point] : null;
                }).filter(Boolean),
                ...epsFit.buildChartLines(futureEpsX),
              }
            : null;
          const chartRev = revFit
            ? {
                historical: revOnly,
                future: futureRevX.map((x) => {
                  const pred = revFit.predict(x);
                  return pred?.point > 0 ? [x, pred.point] : null;
                }).filter(Boolean),
                ...revFit.buildChartLines(futureRevX),
              }
            : null;
          if (!cancelled) {
            setPriceForecastEps(resultEps.length ? resultEps : null);
            setPriceForecastRev(resultRev.length ? resultRev : null);
            setPriceForecastChartEps(chartEps?.historical?.length ? chartEps : null);
            setPriceForecastChartRev(chartRev?.historical?.length ? chartRev : null);
          }
        } else if (!cancelled) {
          setPriceForecastEps(null);
          setPriceForecastRev(null);
          setPriceForecastChartEps(null);
          setPriceForecastChartRev(null);
        }
      };

      const stepCorrelations = () => {
        if (cancelled) return;
        const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
        while (mi < metricKeys.length) {
          const mk = metricKeys[mi];
          const mPtsFor = buildMetricPointsForKey(mk);
          const all = (mPtsFor.length >= 2 && pxForMath.length >= 2) ? calculateCorrelation(mPtsFor, pxForMath) : null;
          results.push({
            metricKey: mk,
            label: METRIC_OPTIONS[mk]?.label || mk,
            all,
            ten: lastN(mPtsFor, 10),
            five: lastN(mPtsFor, 5),
            three: lastN(mPtsFor, 3),
            one: lastN(mPtsFor, 1),
          });
          mi++;
          const now = typeof performance !== "undefined" ? performance.now() : Date.now();
          if (mi < metricKeys.length && now - t0 > 12) break;
        }
        if (mi < metricKeys.length) {
          scheduleIdleTask(stepCorrelations, 500);
        } else if (!cancelled) {
          setCorrelationsByMetric(results);
          setCorrelationsLoading(false);
        }
      };
      scheduleIdleTask(runPriceForecastWork, 400);
      scheduleIdleTask(stepCorrelations, 400);
    } else {
      setCorrelationsByMetric([]);
      setCorrelationsLoading(false);
      setPriceForecastEps(null);
      setPriceForecastRev(null);
      setPriceForecastChartEps(null);
      setPriceForecastChartRev(null);
    }

    return () => { cancelled = true; };
  }, [fundamentalsData, indicator, chartIndicator, activeZScoreYears, comboMembers, stdWindow, maxSigma, isFundamentals, interval, lagDays]);

  const bandsSeries = useMemo(() => {
    if (!showBands || !priceSeries?.length) return [];
    const pxForMath = priceSeries.map(([t,v]) => ({ time: Math.floor(t/1000), value: v }));
    const rb = rollingBands(pxForMath, stdWindow, maxSigma);
    const mean = rb.mean.map(p => [p.time*1000, p.value]);
    const out = [{ name:"Mean", data: mean, dashStyle:"ShortDot", lineWidth:1 }];
    for (let k=1;k<=maxSigma;k++){
      const ser = rb.bands[k] || [];
      out.push({ name:`+${k}σ`, data: ser.map(r=>[r.time*1000, r.upper]), lineWidth:1 });
      out.push({ name:`-${k}σ`, data: ser.map(r=>[r.time*1000, r.lower]), lineWidth:1 });
    }
    return out;
  }, [priceSeries, showBands, stdWindow, maxSigma]);

  const options = useMemo(() => {
    const hiliteSeries =
      (isRealizedPriceIndicator(indicator) && bottomSeries?.length)
        ? buildShadingForRules(bottomSeries, [
            { dir: "above", thr: 0, color: GREEN, label: "Δ > 0" },
            { dir: "below", thr: 0, color: RED, label: "Δ < 0" },
          ])
        : (indicator === "rev_val" && revValBounds?.length && bottomSeries?.length)
        ? buildRevValShading(bottomSeries, revValBounds)
        : (hiliteRulesForIndicator(indicator).length && bottomSeries?.length)
          ? buildShadingForRules(bottomSeries, hiliteRulesForIndicator(indicator))
          : [];
    const bottomPlotLines = buildBottomPlotLines(indicator);

    // Calculate max date for x-axis - extend into future if estimate options are selected
    const isEstimateMode =
      metricKey === "eps_estimate" || metricKey === "revenue_estimate" || metricKey === "revenue_yoy_forecast";
    const yoyMetricDecimals =
      metricKey === "revenue_yoy" || metricKey === "revenue_yoy_forecast" ? 1 : 2;
    const showIndicatorPane = isIndicators;
    const indicatorOnPriceAxis = isRealizedPriceIndicator(indicator);
    const showMetricPane =
      (isFundamentals && metricKey !== "none") ||
      (!isFundamentals && metricSeries?.length > 0);
    const metricOnPriceAxis = metricKey === "dcf" || metricKey === "graham_intrinsic";
    const isMobileChart = window.innerWidth <= 768;

    let priceAxisIdx = 0;
    let metricAxisIdx = 0;
    let indicatorAxisIdx = 1;
    if (showIndicatorPane) {
      if (showMetricPane && !metricOnPriceAxis) {
        metricAxisIdx = 0;
        priceAxisIdx = 1;
        indicatorAxisIdx = 2;
      } else if (showMetricPane && metricOnPriceAxis) {
        priceAxisIdx = 0;
        metricAxisIdx = 0;
        indicatorAxisIdx = 1;
      } else {
        priceAxisIdx = 0;
        indicatorAxisIdx = 1;
      }
    } else if (showMetricPane && !metricOnPriceAxis) {
      metricAxisIdx = 0;
      priceAxisIdx = 1;
    } else {
      priceAxisIdx = 0;
      metricAxisIdx = metricOnPriceAxis ? 0 : 1;
    }

    const priceYAxisConfig = {
      top: 0,
      height: showIndicatorPane ? "68%" : "100%",
      resize: { enabled: true },
      type: isLog ? "logarithmic" : "linear",
      title: isMobileChart
        ? { text: undefined }
        : { text: "Price", style: { color: BRAND.text } },
      labels: {
        align: "left",
        x: isMobileChart ? 2 : 6,
        reserveSpace: true,
        style: { ...AXIS_LABEL_STYLE, ...(isMobileChart ? { fontSize: "10px" } : {}) },
      },
      opposite: true,
      lineWidth: 1,
      lineColor: BRAND.border,
      gridLineWidth: 1,
      gridLineColor: "#2f2f34",
      gridZIndex: 0,
      startOnTick: false,
      endOnTick: false,
      scrollbar: { enabled: !isMobileChart },
      plotLines: [],
    };
    const metricYAxisConfig = {
      ...(metricOnPriceAxis ? { visible: false } : {}),
      top: 0,
      height: showIndicatorPane ? "68%" : "100%",
      opposite: false,
      type: isLog ? "logarithmic" : "linear",
      title: isMobileChart
        ? { text: undefined }
        : { text: METRIC_OPTIONS[metricKey]?.label || "Metric", x: 2, style: { color: BRAND.text } },
      lineWidth: 1,
      lineColor: BRAND.border,
      gridLineWidth: 0,
      gridLineColor: "#2f2f34",
      gridZIndex: 0,
      labels: {
        align: "right",
        x: isMobileChart ? -2 : -4,
        style: { ...AXIS_LABEL_STYLE, ...(isMobileChart ? { fontSize: "10px" } : {}) },
        formatter() {
          const unit = (METRIC_OPTIONS[metricKey]?.unit || "").trim();
          const v = Number(this.value);
          if (unit === "%") return `${v.toFixed(yoyMetricDecimals)}%`;
          if (unit === "$M") return `${v.toFixed(0)}M`;
          if (unit === "$") return `$${v.toFixed(2)}`;
          if (unit === "$B") return `${v.toFixed(2)}B`;
          if (unit === "x") return `${v.toFixed(2)}x`;
          return v.toFixed(2);
        },
      },
      startOnTick: false,
      endOnTick: false,
      scrollbar: { enabled: false },
      plotLines: [{ value: 0, color: BRAND.border, width: 1, zIndex: 1 }],
    };
    const indicatorAxisTitle = isRealizedPriceIndicator(indicator)
      ? "Realized Price Δ (%/bar)"
      : isClassicNovelCombo(chartIndicator)
        ? getComboIndicatorLabel(comboMembers)
        : isZScoreConfigurable(chartIndicator)
          ? getZScoreIndicatorLabel(chartIndicator, activeZScoreYears)
          : (INDICATOR_OPTIONS[indicator] || "Indicator");
    const indicatorYAxisConfig = {
      top: "68%",
      height: "32%",
      offset: 0,
      resize: { enabled: true },
      opposite: true,
      type: "linear",
      // Long titles steal horizontal width on phones; name already shows above the chart.
      title: isMobileChart
        ? { text: undefined }
        : {
            text: indicatorAxisTitle,
            style: { color: BRAND.text },
            align: "middle",
          },
      labels: {
        align: "right",
        x: isMobileChart ? 4 : 8,
        reserveSpace: true,
        enabled: true,
        style: { ...AXIS_LABEL_STYLE, ...(isMobileChart ? { fontSize: "10px" } : {}) },
        formatter() {
          const v = this.value;
          if (!Number.isFinite(v)) return v ?? "";
          const asPercent =
            indicator === "revenue_cagr" ||
            indicator === "eps_cagr" ||
            isRealizedPriceIndicator(indicator);
          return formatIndicatorValue(v, { asPercent });
        },
      },
      lineWidth: 2,
      lineColor: BRAND.border,
      gridLineWidth: 2,
      gridLineColor: "#2f2f34",
      gridZIndex: 0,
      startOnTick: false,
      endOnTick: false,
      scrollbar: { enabled: !isMobileChart },
      plotLines: bottomPlotLines,
    };

    const nonOverviewYAxis = (() => {
      if (showIndicatorPane) {
        if (showMetricPane && !metricOnPriceAxis) {
          return [metricYAxisConfig, priceYAxisConfig, indicatorYAxisConfig];
        }
        if (showMetricPane && metricOnPriceAxis) {
          return [priceYAxisConfig, indicatorYAxisConfig, metricYAxisConfig];
        }
        return [priceYAxisConfig, indicatorYAxisConfig];
      }
      if (showMetricPane && !metricOnPriceAxis) {
        return [metricYAxisConfig, priceYAxisConfig];
      }
      if (showMetricPane && metricOnPriceAxis) {
        return [priceYAxisConfig, metricYAxisConfig];
      }
      return [priceYAxisConfig];
    })();
    // Highcharts x-extremes use every series; clip per-share metrics to the same [tMin,tMax] as Close
    // so intrinsic value cannot draw before the first price bar (e.g. fundamentals from the 1980s).
    let metricSeriesForChart = metricSeries;
    let dcfSeriesForChart = dcfSeries;
    if (
      metricOnPriceAxis &&
      Array.isArray(priceSeries) &&
      priceSeries.length > 0
    ) {
      const tLo = priceSeries[0][0];
      const tHi = priceSeries[priceSeries.length - 1][0];
      if (Array.isArray(metricSeries) && metricSeries.length > 0) {
        metricSeriesForChart = metricSeries.filter(
          (pt) => pt && Number.isFinite(pt[0]) && pt[0] >= tLo && pt[0] <= tHi
        );
      }
      if (Array.isArray(dcfSeries) && dcfSeries.length > 0) {
        dcfSeriesForChart = dcfSeries.filter(
          (pt) => pt && Number.isFinite(pt[0]) && pt[0] >= tLo && pt[0] <= tHi
        );
      }
    }
    let xAxisMax = null;
    if (isEstimateMode && (estSeries?.length > 0 || pricePlaceholderSeries?.length > 0)) {
      const maxEstimateDate = estSeries?.length ? Math.max(...estSeries.map(p => p[0])) : 0;
      const lastPriceDate = priceSeries?.length ? priceSeries[priceSeries.length - 1][0] : 0;
      const lastPlaceholderDate = pricePlaceholderSeries?.length
        ? pricePlaceholderSeries[pricePlaceholderSeries.length - 1][0]
        : 0;
      // Cap at real series endpoints only — a large empty buffer past the last point makes
      // Highcharts Stock's ordinal axis stretch the forecast years vs history.
      const maxDate = Math.max(maxEstimateDate, lastPriceDate, lastPlaceholderDate);
      xAxisMax = maxDate + (14 * 24 * 60 * 60 * 1000);
    }

    const chartHeight = isMobileChart ? 500 : 680;
    const iconsBase = (typeof process !== "undefined" && process.env?.PUBLIC_URL) ? process.env.PUBLIC_URL : "";
    const stockIconsURL = `${iconsBase}/stock-icons/`;
    return {
      stockTools: {
        gui: {
          enabled: !isMobileChart,
        },
      },
      navigation: {
        iconsURL: stockIconsURL,
        annotationsOptions: {
          shapeOptions: {
            stroke: "#ffffff",
            strokeWidth: 12,
          },
          line: {
            stroke: "#ffffff",
            strokeWidth: 12,
          },
          typeOptions: {
            line: {
              stroke: "#ffffff",
              strokeWidth: 12,
            },
          },
          /* Tracker hit area = strokeWidth + snap*2; we override visible stroke to 0.25 in CSS/thinAnnotationStrokes (tracker excluded) */
          snap: 5,
        },
        // When in indicators mode, toolbar "Indicators" button opens our indicator modal instead of Highcharts built-in list
        ...(showIndicatorPane ? {
          bindings: {
            indicators: {
              init: function() {
                if (this.chart.openIndicatorsModal) this.chart.openIndicatorsModal();
              },
            },
          },
        } : {}),
      },
      chart: {
        height: chartHeight,
        backgroundColor: BRAND.bg,
        zooming: { type: "xy", mouseWheel: !isMobileChart },
        panning: { enabled: true, type: "xy" },
        spacing: isMobileChart ? [8, 4, 8, 4] : [10, 10, 15, 10],
        style: { fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif" },
        events: {
          load: function() {
            // Set x-axis max after chart loads to show future estimates
            const chart = this;
            if (isEstimateMode && xAxisMax) {
              // Use setTimeout to ensure chart is fully initialized; re-check axes (remount may destroy chart)
              setTimeout(() => extendXAxisMax(chart, xAxisMax), 100);
            }
            thinAnnotationStrokes(chart);
            setTimeout(() => thinAnnotationStrokes(chart), 100);
            if (showIndicatorPane) chart.openIndicatorsModal = () => setIndModal(true);
            // Invalidate pointer’s chart position when starting annotation/control-point interaction
            // so drag uses fresh coordinates and doesn’t jump (e.g. when popup or layout shifted)
            function invalidatePositionForAnnotation(e) {
              const t = e.target;
              if (!chart.pointer || !chart.container?.contains(t)) return;
              if (t.closest && (t.closest(".highcharts-annotation") || t.closest(".highcharts-control-points"))) {
                chart.pointer.chartPosition = void 0;
                // When indicator pane is on, clear again next frame so first mousemove recalculates
                if (showIndicatorPane) requestAnimationFrame(() => { if (chart.pointer) chart.pointer.chartPosition = void 0; });
              }
            }
            chart.container?.addEventListener("mousedown", invalidatePositionForAnnotation, true);
            chart.container?.addEventListener("touchstart", invalidatePositionForAnnotation, true);
          },
          redraw: function() {
            // Also set on redraw to ensure it persists
            const chart = this;
            if (isEstimateMode && xAxisMax) {
              extendXAxisMax(chart, xAxisMax);
            }
            thinAnnotationStrokes(chart);
            setTimeout(() => thinAnnotationStrokes(chart), 50);
            // After layout changes (indicator/fundamental toggled), pointer cache can be stale and cause annotation drag to jump
            if (chart.pointer) chart.pointer.chartPosition = void 0;
            // When indicator pane is on, layout can settle after redraw; clear cache again so first interaction uses correct position
            if (showIndicatorPane && chart.pointer) {
              const clear = () => { if (chart.pointer) chart.pointer.chartPosition = void 0; };
              requestAnimationFrame(() => { clear(); setTimeout(clear, 20); });
            }
          }
        }
      },
      time: { useUTC: true },
      credits: { enabled: false },

      rangeSelector: {
        inputEnabled: false,
        selected: isEstimateMode ? null : 5, // Don't auto-select range when in estimate mode
        buttons: [
          { type: "month", count: 6, text: "6m" },
          { type: "ytd", text: "YTD" },
          { type: "year", count: 1, text: "1Y" },
          { type: "year", count: 3, text: "3Y" },
          { type: "year", count: 5, text: "5Y" }
        ],
        events: {
          // Prevent rangeSelector from constraining to price data
          afterButtonClick: function(e) {
            if (isEstimateMode && xAxisMax && this.chart) {
              setTimeout(() => extendXAxisMax(this.chart, xAxisMax), 50);
            }
          }
        },
        buttonPosition: { align: "left", x: isMobileChart ? 8 : 16, y: 0 },
        buttonTheme: {
          fill: BRAND.panel, 
          stroke: BRAND.border, 
          r: isMobileChart ? 6 : 8, 
          style: { 
            color: BRAND.text, 
            fontWeight: 600,
            fontSize: isMobileChart ? "11px" : "12px"
          },
          states: { select: { fill: BRAND.text, style: { color: "#1b1b1f" } } }
        },
        labelStyle: { color: BRAND.muted, fontWeight: 500, fontSize: isMobileChart ? "11px" : "12px" }
      },

      navigator: {
        enabled: true,
        outlineColor: BRAND.border,
        maskFill: "rgba(215,215,217,0.1)",
        series: { color: BRAND.muted }
      },
      scrollbar: {
        enabled: true,
        barBackgroundColor: BRAND.mid,
        buttonBackgroundColor: BRAND.mid,
        trackBorderColor: BRAND.border,
        rifleColor: BRAND.text
      },

      xAxis: [
        {
          ordinal: !isEstimateMode,
          crosshair: { snap: true },
          lineWidth: 1,
          lineColor: BRAND.border,
          gridLineWidth: 0,
          gridLineColor: BRAND.border,
          labels: { style: { ...AXIS_LABEL_STYLE } },
          ...(xAxisMax ? { max: xAxisMax, minRange: null } : {}),
        },
        { linkedTo: 0, top: 0, height: showIndicatorPane ? "68%" : "100%", opposite: true, lineWidth: 1, lineColor: BRAND.border, tickLength: 0, labels: { enabled: false }, gridLineWidth: 0 },
        ...(showIndicatorPane ? [
          { linkedTo: 0, top: "68%", height: "32%", opposite: true, lineWidth: 1, lineColor: BRAND.border, tickLength: 0, labels: { enabled: false }, gridLineWidth: 0 }
        ] : [])
      ],

      yAxis: nonOverviewYAxis,

      tooltip: {
        shared: true,
        split: false,
        stickOnContact: true,
        backgroundColor: BRAND.panel,
        borderColor: BRAND.border,
        style: { color: BRAND.text }
      },

      plotOptions: {
        series: {
          animation: false,
          turboThreshold: 0,
          dataGrouping: { enabled: true },
          states: {
            inactive: { opacity: 1 },
          },
        },
        arearange: {
          lineWidth: 0,
          marker: { enabled: false },
          enableMouseTracking: false,
          dataGrouping: { enabled: false },
          zIndex: 0
        }
      },

      series: [
            { type: "line", name: "Close", data: priceSeries, yAxis: priceAxisIdx, tooltip: { valueDecimals: 2 }, color: BRAND.accent, lineWidth: 2.2 },
            ...(pricePlaceholderSeries?.length > 0 ? [{
              type: "line",
              name: "",
              data: pricePlaceholderSeries,
              yAxis: priceAxisIdx,
              color: BRAND.bg,
              lineWidth: 1.4,
              enableMouseTracking: false,
              showInLegend: false,
              zIndex: 0,
              dataGrouping: { enabled: false },
            }] : []),
            ...(showMetricPane ? [{
              type: metricDisplayType === "column" ? "column" : "line",
              name: metricKey === "revenue_yoy_forecast" ? "Actual YoY" : "Metric",
              data: metricSeriesForChart,
              yAxis: metricOnPriceAxis ? priceAxisIdx : metricAxisIdx,
              color: METRIC_LINE_COLOR,
              ...(metricDisplayType === "column"
                ? {
                    pointRange: medianPointRangeMs(metricSeriesForChart),
                    pointPadding: 0.12,
                    groupPadding: 0.08,
                    borderWidth: 0,
                    borderColor: METRIC_LINE_COLOR,
                  }
                : {
                    lineWidth: 2.2,
                    dashStyle: "ShortDash",
                  }),
              dataGrouping: {
                enabled:
                  metricDisplayType !== "column" &&
                  metricKey !== "revenue_yoy_forecast" &&
                  metricKey !== "revenue_yoy",
              },
              tooltip: {
                valueDecimals: yoyMetricDecimals,
                valueSuffix: (METRIC_OPTIONS[metricKey]?.unit === "%") ? "%" : (METRIC_OPTIONS[metricKey]?.unit === "$B") ? "B" : (METRIC_OPTIONS[metricKey]?.unit === "$M") ? "M" : (METRIC_OPTIONS[metricKey]?.unit === "x") ? "x" : ""
              }
            }] : []),
            ...(showMetricPane && estSeries?.length ? [{
              type: metricDisplayType === "column" ? "column" : "line",
              name: metricKey === "revenue_yoy_forecast" ? "Forecast YoY" : "Forecast",
              data: estSeries,
              yAxis: metricOnPriceAxis ? priceAxisIdx : metricAxisIdx,
              color: (metricKey === "eps_estimate" || metricKey === "revenue_estimate" || metricKey === "revenue_yoy_forecast") ? METRIC_FORECAST_COLOR : METRIC_LINE_COLOR,
              ...(metricDisplayType === "column"
                ? {
                    pointRange: medianPointRangeMs(estSeries),
                    pointPadding: 0.12,
                    groupPadding: 0.08,
                    borderWidth: 0,
                    borderColor: (metricKey === "eps_estimate" || metricKey === "revenue_estimate" || metricKey === "revenue_yoy_forecast") ? METRIC_FORECAST_COLOR : METRIC_LINE_COLOR,
                  }
                : {
                    lineWidth: 2.2,
                    dashStyle: "ShortDot",
                  }),
              dataGrouping: { enabled: false },
              connectNulls: false,
              tooltip: {
                valueDecimals: yoyMetricDecimals,
                valueSuffix: (METRIC_OPTIONS[metricKey]?.unit === "%") ? "%" : (METRIC_OPTIONS[metricKey]?.unit === "$B") ? "B" : ""
              }
            }] : []),
            ...(showMetricPane && dcfSeriesForChart?.length ? [{
              type: metricDisplayType === "column" ? "column" : "line",
              name: "DCF",
              data: dcfSeriesForChart,
              yAxis: metricOnPriceAxis ? priceAxisIdx : metricAxisIdx,
              color: "#d1d5db",
              ...(metricDisplayType === "column"
                ? {
                    pointRange: medianPointRangeMs(dcfSeriesForChart),
                    pointPadding: 0.12,
                    groupPadding: 0.08,
                    borderWidth: 0,
                  }
                : { lineWidth: 2 }),
              tooltip: { valuePrefix: (METRIC_OPTIONS[metricKey]?.unit === "$" ? "$" : "") }
            }] : []),
            ...(indicatorOnPriceAxis && realizedPriceOverlaySeries?.length
              ? splitHighchartsLineBySlope(realizedPriceOverlaySeries).map((seg, idx) => ({
                  type: "line",
                  name: idx === 0 ? (INDICATOR_OPTIONS[indicator] || "Realized Price") : undefined,
                  showInLegend: idx === 0,
                  linkedTo: idx > 0 ? ":previous" : undefined,
                  data: seg.data,
                  yAxis: priceAxisIdx,
                  lineWidth: 1.2,
                  dashStyle: "ShortDash",
                  color: seg.color,
                  tooltip: { valueDecimals: 2, valuePrefix: "$" },
                  dataGrouping: { enabled: false },
                  zIndex: 2,
                }))
              : []),
            ...(showIndicatorPane ? hiliteSeries.map((s) => ({ ...s, yAxis: indicatorAxisIdx })) : []),
            ...(showIndicatorPane && isRealizedPriceIndicator(indicator) && bottomSeries?.length
              ? splitHighchartsLineByValueSign(bottomSeries).map((seg, idx) => ({
                  type: "line",
                  name: idx === 0 ? "Realized Price Δ (%/bar)" : undefined,
                  showInLegend: idx === 0,
                  linkedTo: idx > 0 ? ":previous" : undefined,
                  data: seg.data,
                  yAxis: indicatorAxisIdx,
                  lineWidth: 1.2,
                  color: seg.color,
                  tooltip: { valueDecimals: 3, valueSuffix: "%" },
                  dataGrouping: { enabled: false },
                  enableMouseTracking: true,
                  zIndex: 3,
                }))
              : []),
            ...(showIndicatorPane && !isRealizedPriceIndicator(indicator) ? [{
              type: "line",
              name: (indicator === "revenue_cagr" || indicator === "eps_cagr") ? "2y CAGR" : "Indicator",
              data: bottomSeries,
              yAxis: indicatorAxisIdx,
              tooltip: {
                valueDecimals: 2,
                valueSuffix: (indicator === "revenue_cagr" || indicator === "eps_cagr") ? "%" : undefined,
              },
              color: "#e11d48",
              lineWidth: 1,
              dataGrouping: { enabled: false },
              enableMouseTracking: true,
            }] : []),
            ...(showIndicatorPane && bottomSeriesAlt?.length ? [{
              type: "line",
              name: "1y CAGR",
              data: bottomSeriesAlt,
              yAxis: indicatorAxisIdx,
              tooltip: { valueDecimals: 2, valueSuffix: "%" },
              color: "#3b82f6",
              lineWidth: 1,
              dataGrouping: { enabled: false },
              enableMouseTracking: true,
            }] : []),
            ...(showIndicatorPane && bottomSeriesFwd?.length ? [{
              type: "line",
              name: "Fwd 1y est",
              data: bottomSeriesFwd,
              yAxis: indicatorAxisIdx,
              tooltip: { valueDecimals: 2, valueSuffix: "%" },
              color: "#10b981",
              dashStyle: "ShortDot",
              lineWidth: 1.2,
              dataGrouping: { enabled: false },
              enableMouseTracking: true,
            }] : []),
            ...bandsSeries.map(s => ({ ...s, yAxis: priceAxisIdx, color: BRAND.border }))
      ]
    };
  }, [priceSeries, pricePlaceholderSeries, metricSeries, estSeries, dcfSeries, bottomSeries, realizedPriceOverlaySeries, bottomSeriesAlt, bottomSeriesFwd, isLog, bandsSeries, metricKey, metricDisplayType, indicator, comboMembers, revValBounds, isFundamentals, isIndicators]);

  // Keep x-axis max aligned to estimate/placeholder endpoints (no large empty overshoot).
  useEffect(() => {
    const isEstimateMode =
      metricKey === "eps_estimate" || metricKey === "revenue_estimate" || metricKey === "revenue_yoy_forecast";
    if (isEstimateMode && (estSeries?.length > 0 || pricePlaceholderSeries?.length > 0) && chartRef.current?.chart) {
      const maxEstimateDate = estSeries?.length ? Math.max(...estSeries.map(p => p[0])) : 0;
      const lastPlaceholderDate = pricePlaceholderSeries?.length ? pricePlaceholderSeries[pricePlaceholderSeries.length - 1][0] : 0;
      const lastPriceDate = priceSeries?.length ? priceSeries[priceSeries.length - 1][0] : 0;
      const xAxisMax = Math.max(maxEstimateDate, lastPlaceholderDate, lastPriceDate) + (14 * 24 * 60 * 60 * 1000);
      const chart = chartRef.current.chart;
      const t = setTimeout(() => extendXAxisMax(chartRef.current?.chart || chart, xAxisMax), 200);
      return () => clearTimeout(t);
    }
  }, [estSeries, metricKey, priceSeries, pricePlaceholderSeries]);

  return (
    <div style={{ color: BRAND.text }}>
      {/* Top toolbar - Controls only (Resolution, Log scale) */}
      <div style={{
        display:"flex", 
        flexWrap: isMobile ? "wrap" : "nowrap",
        gap: isMobile ? 6 : 8, 
        alignItems:"center", 
        padding: isMobile ? "12px" : "10px 10px",
        marginBottom:8,
        background: BRAND.panel, 
        border: `1px solid ${BRAND.border}`, 
        borderRadius: 12,
        justifyContent: "flex-start" // Align to left
      }}>
        <div style={{ color: BRAND.text, display: "flex", alignItems: "center", gap: 4 }}>
          <label style={{ fontSize: isMobile ? "12px" : "14px", display: "flex", alignItems: "center", gap: 4 }}>
            {!isMobile && "Resolution:"}
            <select
              value={interval}
              onChange={e=>setInterval(e.target.value)}
              style={{ 
                background: BRAND.bg, 
                color: BRAND.text, 
                border:`1px solid ${BRAND.border}`, 
                borderRadius:8, 
                padding: isMobile ? "6px 8px" : "4px 8px",
                fontSize: isMobile ? "12px" : "14px",
                minHeight: "36px"
              }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
        </div>

        <button
          type="button"
          role="checkbox"
          aria-checked={isLog}
          aria-label="Log scale"
          onClick={() => setIsLog(!isLog)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
            whiteSpace: "nowrap",
            padding: isMobile ? "6px 8px" : "4px 8px",
            minHeight: isMobile ? 36 : undefined,
            fontSize: isMobile ? 12 : 14,
            color: BRAND.text,
            background: BRAND.bg,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          <span
            aria-hidden
            style={{
              width: 14,
              height: 14,
              boxSizing: "border-box",
              flexShrink: 0,
              borderRadius: 3,
              border: `1.5px solid ${isLog ? BRAND.accent : BRAND.borderLight}`,
              background: isLog ? BRAND.accent : "transparent",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLog && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5.2L4.1 7.2L8 2.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          Log scale
        </button>

        {metricKey !== "none" && (
          <label style={{ fontSize: isMobile ? "12px" : "14px", display: "flex", alignItems: "center", gap: 4 }}>
            {!isMobile && "Fundamental:"}
            <select
              value={metricDisplayType}
              onChange={(e) => setMetricDisplayType(e.target.value)}
              style={{
                background: BRAND.bg,
                color: BRAND.text,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 8,
                padding: isMobile ? "6px 8px" : "4px 8px",
                fontSize: isMobile ? "12px" : "14px",
                minHeight: "36px",
              }}
              title="Fundamental chart style"
            >
              <option value="line">Line</option>
              <option value="column">Bar</option>
            </select>
          </label>
        )}

        {metricKey !== "none" && (
          <label style={{ fontSize: isMobile ? "12px" : "14px", display: "flex", alignItems: "center", gap: 4 }}>
            {!isMobile && "Period:"}
            <select
              value={metricPeriod}
              onChange={(e) => setMetricPeriod(e.target.value === METRIC_PERIOD_YEAR ? METRIC_PERIOD_YEAR : METRIC_PERIOD_QUARTER)}
              style={{
                background: BRAND.bg,
                color: BRAND.text,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 8,
                padding: isMobile ? "6px 8px" : "4px 8px",
                fontSize: isMobile ? "12px" : "14px",
                minHeight: "36px",
              }}
              title="Quarterly or yearly fundamental data"
            >
              <option value={METRIC_PERIOD_QUARTER}>Quarterly</option>
              <option value={METRIC_PERIOD_YEAR}>Yearly</option>
            </select>
          </label>
        )}

        {isIndicators && isClassicNovelCombo(chartIndicator) && (
          <button
            type="button"
            onClick={() => setComboModal(true)}
            style={{
              background: BRAND.bg,
              color: BRAND.text,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 8,
              padding: isMobile ? "6px 10px" : "4px 10px",
              fontSize: isMobile ? "12px" : "14px",
              cursor: "pointer",
              minHeight: "36px",
            }}
            title="Choose which Classic/Novel indicators to combine"
          >
            Members: {comboMembers.length}
          </button>
        )}

        {isIndicators && isZScoreConfigurable(chartIndicator) && (
          <button
            type="button"
            onClick={() => setZScoreModal({ open: true, indicator: chartIndicator })}
            style={{
              background: BRAND.bg,
              color: BRAND.text,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 8,
              padding: isMobile ? "6px 10px" : "4px 10px",
              fontSize: isMobile ? "12px" : "14px",
              cursor: "pointer",
              minHeight: "36px",
            }}
            title="Configure Z-score lookback years"
          >
            Lookback: {formatZScoreYears(activeZScoreYears)}
          </button>
        )}
      </div>

      <div
        style={{
          marginBottom: 6,
          fontWeight: 700,
          fontSize: isMobile ? "14px" : "16px",
          padding: isMobile ? "0 4px" : "0",
          paddingLeft: isMobile ? "calc(4px + 2ch)" : "2ch",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        {companyLogoUrl ? (
          <img
            src={companyLogoUrl}
            alt=""
            style={companyLogoTitleStyle(isMobile ? COMPANY_LOGO_TITLE_SIZE_MOBILE : COMPANY_LOGO_TITLE_SIZE)}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : null}
        <span>
          {isFundamentals && `${symbol} — ${metricKey === "none" ? "Price" : `Price vs ${METRIC_OPTIONS[metricKey]?.label} (${metricPeriod === METRIC_PERIOD_YEAR ? "Yearly" : "Quarterly"})`}`}
          {isIndicators && `${symbol} — ${
            isClassicNovelCombo(chartIndicator)
              ? getComboIndicatorLabel(comboMembers)
              : isZScoreConfigurable(chartIndicator)
              ? getZScoreIndicatorLabel(chartIndicator, activeZScoreYears)
              : (INDICATOR_OPTIONS[indicator] || "Indicator")
          }${metricSeries?.length ? ` · ${METRIC_OPTIONS[metricKey]?.label} (${metricPeriod === METRIC_PERIOD_YEAR ? "Yearly" : "Quarterly"})` : ""}`}
        </span>
      </div>

      <HighchartsReact 
        highcharts={Highcharts} 
        constructorType="stockChart" 
        options={options}
        ref={chartRef}
        key={`${symbol}-${mode}-${metricKey}-${metricDisplayType}-${metricPeriod}`}
      />

      {/* Summary + forecasts — only on Fundamentals page */}
      {isFundamentals && (
      <>
        {/* Correlation with price — separate panel */}
        <div style={{ marginTop:12, padding:12, ...surface, background: BRAND.panel }}>
          <div style={{ fontWeight:600, marginBottom:8 }}>Correlation with price</div>
          {correlationsLoading ? (
            <div style={{ display:"flex", alignItems:"center", gap:8, color: BRAND.muted, fontSize:14 }}>
              <span style={{ fontSize:18 }}>⟳</span>
              <span>Calculating correlations…</span>
            </div>
          ) : (
            <div style={{ overflow:"auto", maxHeight: 180 }}>
              <table style={{ borderCollapse:"collapse", width:"100%", minWidth:480, fontSize:14 }}>
                <thead style={{ position:"sticky", top:0, background: BRAND.panel, zIndex:1 }}>
                  <tr>
                    <th style={{ textAlign:"left", padding:"6px 10px 6px 0", fontWeight:600, borderBottom:"2px solid #e6e6e6" }}>Fundamental</th>
                    <th style={{ textAlign:"right", padding:"6px 10px", fontWeight:600, cursor:"pointer", borderBottom:"2px solid #e6e6e6" }} onClick={()=>setCorrelationSortBy("all")} title="Sort by All">All {correlationSortBy==="all"?"▼":""}</th>
                    <th style={{ textAlign:"right", padding:"6px 10px", fontWeight:600, cursor:"pointer", borderBottom:"2px solid #e6e6e6" }} onClick={()=>setCorrelationSortBy("ten")} title="Sort by 10y">10y {correlationSortBy==="ten"?"▼":""}</th>
                    <th style={{ textAlign:"right", padding:"6px 10px", fontWeight:600, cursor:"pointer", borderBottom:"2px solid #e6e6e6" }} onClick={()=>setCorrelationSortBy("five")} title="Sort by 5y">5y {correlationSortBy==="five"?"▼":""}</th>
                    <th style={{ textAlign:"right", padding:"6px 10px", fontWeight:600, cursor:"pointer", borderBottom:"2px solid #e6e6e6" }} onClick={()=>setCorrelationSortBy("three")} title="Sort by 3y">3y {correlationSortBy==="three"?"▼":""}</th>
                    <th style={{ textAlign:"right", padding:"6px 10px", fontWeight:600, cursor:"pointer", borderBottom:"2px solid #e6e6e6" }} onClick={()=>setCorrelationSortBy("one")} title="Sort by 1y">1y {correlationSortBy==="one"?"▼":""}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...correlationsByMetric]
                    .sort((a,b)=>{
                      const col = correlationSortBy;
                      const va = a[col];
                      const vb = b[col];
                      const na = va == null ? -Infinity : va;
                      const nb = vb == null ? -Infinity : vb;
                      return nb - na;
                    })
                    .map(r=>(
                      <tr key={r.metricKey} style={{ borderBottom:"1px solid #e6e6e6" }}>
                        <td style={{ padding:"6px 10px 6px 0" }}>{r.label}</td>
                        <td style={{ textAlign:"right", padding:"6px 10px" }}>{r.all==null?"—":r.all.toFixed(3)}</td>
                        <td style={{ textAlign:"right", padding:"6px 10px" }}>{r.ten==null?"—":r.ten.toFixed(3)}</td>
                        <td style={{ textAlign:"right", padding:"6px 10px" }}>{r.five==null?"—":r.five.toFixed(3)}</td>
                        <td style={{ textAlign:"right", padding:"6px 10px" }}>{r.three==null?"—":r.three.toFixed(3)}</td>
                        <td style={{ textAlign:"right", padding:"6px 10px" }}>{r.one==null?"—":r.one.toFixed(3)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Next 8 quarters (analyst consensus) + price forecast — separate panel */}
        {fundamentalsData && (
          <div style={{ marginTop:20, padding:12, ...surface, background: BRAND.panel }}>
            {(() => {
              const af = fundamentalsData?.fundamentals?.analystForecasts;
              const epsForward = forwardEstimateRows(af?.eps);
              const revForward = forwardEstimateRows(af?.revenue);
              const hasData = epsForward.length > 0 || revForward.length > 0;
              const quarterDates = (epsForward.length >= revForward.length ? epsForward : revForward).map(r => r.date);
              const displayDates = quarterDates.length
                ? quarterDates
                : Array.from({ length: FORWARD_ESTIMATE_QUARTERS }, () => "—");
              return (
                <>
                  <div style={{ fontWeight:700, marginBottom:6 }}>Next 8 quarters (analyst consensus)</div>
                  {!af ? (
                    <div style={{ marginTop:6, color: BRAND.muted }}>Loading…</div>
                  ) : !hasData ? (
                    <div style={{ marginTop:6, color: BRAND.muted }}>No forward items available.</div>
                  ) : (
                    <div style={{ overflowX:"auto", marginTop:6 }}>
                      <table style={{ borderCollapse:"collapse", width:"100%", minWidth:360, fontSize:14 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign:"left", padding:"8px 10px", borderBottom:"2px solid #e6e6e6", fontWeight:600 }}>Metric</th>
                            {displayDates.slice(0, FORWARD_ESTIMATE_QUARTERS).map((d, i) => (
                              <th key={i} style={{ textAlign:"right", padding:"8px 10px", borderBottom:"2px solid #e6e6e6", fontWeight:600 }}>{d}</th>
                            ))}
                            <th style={{ textAlign:"right", padding:"8px 10px", borderBottom:"2px solid #e6e6e6", fontWeight:600 }}>Forecast accuracy (5y)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ padding:"8px 10px", borderBottom:"1px solid #e6e6e6", fontWeight:600 }}>EPS</td>
                            {Array.from({ length: FORWARD_ESTIMATE_QUARTERS }, (_, i) => {
                              const r = epsForward[i];
                              const display = r?.estimate != null ? Number(r.estimate).toFixed(2) : "—";
                              return <td key={i} style={{ textAlign:"right", padding:"8px 10px", borderBottom:"1px solid #e6e6e6" }}>{display}</td>;
                            })}
                            <td style={{ textAlign:"right", padding:"8px 10px", borderBottom:"1px solid #e6e6e6", color: BRAND.muted }}>
                              {(af?.eps?.accuracy5yPct ?? af?.eps?.accuracy) != null ? `${(af.eps.accuracy5yPct ?? af.eps.accuracy).toFixed(1)}%` : "—"}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding:"8px 10px", borderBottom:"1px solid #e6e6e6", fontWeight:600 }}>Revenue</td>
                            {Array.from({ length: FORWARD_ESTIMATE_QUARTERS }, (_, i) => {
                              const r = revForward[i];
                              const display = r?.estimate != null ? `${(r.estimate / 1e9).toFixed(2)}B` : "—";
                              return <td key={i} style={{ textAlign:"right", padding:"8px 10px", borderBottom:"1px solid #e6e6e6" }}>{display}</td>;
                            })}
                            <td style={{ textAlign:"right", padding:"8px 10px", borderBottom:"1px solid #e6e6e6", color: BRAND.muted }}>
                              {(af?.revenue?.accuracy5yPct ?? af?.revenue?.accuracy) != null ? `${(af.revenue.accuracy5yPct ?? af.revenue.accuracy).toFixed(1)}%` : "—"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
            <div style={{ marginTop:10, padding:10, background: BRAND.bgSecondary, borderRadius:8, fontSize:13, color: BRAND.textSecondary }}>
              <strong>Forecast accuracy vs correlation:</strong> Forecast accuracy measures how often analyst estimates matched actual results over the last 5 years. Correlation measures how closely a fundamental metric moves with price historically. High correlation means the metric tends to track price; high accuracy means analyst forecasts tend to be correct.
            </div>
          </div>
        )}

        {/* Price forecast (next 8 quarters) — separate panel, table */}
        {(priceForecastEps?.length > 0 || priceForecastRev?.length > 0) && (
          <div style={{ marginTop:20, padding:12, ...surface, background: BRAND.panel }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>Price forecast (next 8 quarters)</div>
            <div style={{ fontSize:13, color: BRAND.muted, marginBottom:10 }}>Linear regression of historic price vs analyst EPS/Revenue estimates. Predictions use analyst consensus for the next 8 quarters with a 95% prediction interval. Historical dots use pre-report estimates; forecast dots (orange) show model-implied prices for forward estimates.</div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ borderCollapse:"collapse", width:"100%", minWidth:480, fontSize:14 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign:"left", padding:"8px 10px", borderBottom:"2px solid #e6e6e6", fontWeight:600 }}>Model</th>
                    {Array.from({ length: FORWARD_ESTIMATE_QUARTERS }, (_, i) => {
                      const r = (priceForecastEps?.length ? priceForecastEps : priceForecastRev)?.[i];
                      return <th key={i} style={{ textAlign:"right", padding:"8px 10px", borderBottom:"2px solid #e6e6e6", fontWeight:600 }}>{r?.date ?? "—"}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding:"8px 10px", borderBottom:"1px solid #e6e6e6", fontWeight:600 }}>Based on EPS model</td>
                    {Array.from({ length: FORWARD_ESTIMATE_QUARTERS }, (_, i) => {
                      const r = priceForecastEps?.[i];
                      return (
                        <td key={i} style={{ textAlign:"right", padding:"8px 10px", borderBottom:"1px solid #e6e6e6" }}>
                          {r ? `$${r.point.toFixed(2)} (95% CI: $${r.low.toFixed(2)} – $${r.high.toFixed(2)})` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td style={{ padding:"8px 10px", borderBottom:"1px solid #e6e6e6", fontWeight:600 }}>Based on Revenue model</td>
                    {Array.from({ length: FORWARD_ESTIMATE_QUARTERS }, (_, i) => {
                      const r = priceForecastRev?.[i];
                      return (
                        <td key={i} style={{ textAlign:"right", padding:"8px 10px", borderBottom:"1px solid #e6e6e6" }}>
                          {r ? `$${r.point.toFixed(2)} (95% CI: $${r.low.toFixed(2)} – $${r.high.toFixed(2)})` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            {(priceForecastChartEps || priceForecastChartRev) && (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginTop: 8 }}>
                {priceForecastChartEps && (
                  <PriceForecastRegressionChart
                    title="EPS estimate vs price"
                    xLabel="EPS estimate ($)"
                    yLabel="Price ($)"
                    chartData={priceForecastChartEps}
                    ticker={symbol}
                  />
                )}
                {priceForecastChartRev && (
                  <PriceForecastRegressionChart
                    title="Revenue estimate vs price"
                    xLabel="Revenue estimate ($B)"
                    yLabel="Price ($)"
                    chartData={priceForecastChartRev}
                    ticker={symbol}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </>
      )}

      {isIndicators && <IndicatorCalcBlock calc={indicatorCalc} />}

      {isIndicators && customStrategyResult && <IndicatorCalcBlock calc={customStrategyResult} />}

      {isIndicators && <IndicatorDescriptionBlock indicatorKey={chartIndicator} zScoreYears={activeZScoreYears} />}

      {/* Modals */}
      <IndicatorsModal
        open={indModal}
        onClose={()=>setIndModal(false)}
        indicator={indicator}
        onPickIndicator={handlePickIndicator}
        zScoreYearsMap={zScoreYearsMap}
        comboMembers={comboMembers}
      />
      <ZScoreYearsModal
        open={zScoreModal.open}
        indicatorKey={zScoreModal.indicator || chartIndicator}
        yearsMap={zScoreYearsMap}
        onClose={() => setZScoreModal({ open: false, indicator: null })}
        onConfirm={handleZScoreYearsConfirm}
      />
      <ComboMembersModal
        open={comboModal}
        onClose={() => setComboModal(false)}
        members={comboMembers}
        zScoreYearsMap={zScoreYearsMap}
        onConfirm={handleComboMembersConfirm}
      />
      <MetricsModal
        open={metModal}
        onClose={()=>setMetModal(false)}
        metricKey={metricKey}
        setMetricKey={setMetricKey}
        metricDisplayType={metricDisplayType}
        setMetricDisplayType={setMetricDisplayType}
      />
      {fundamentalsData && (
        <CustomStrategyModal
          open={customStrategyModal}
          onClose={()=>setCustomStrategyModal(false)}
          indicator={chartIndicator}
          indicatorSeries={(() => {
            const { seriesCache } = fundamentalsData;
            const pickBottom = () => {
              if (isClassicNovelCombo(chartIndicator)) {
                return getSeriesForIndicatorKey(seriesCache, CLASSIC_NOVEL_COMBO_KEY, activeZScoreYears, comboExtra);
              }
              if (isZScoreConfigurable(chartIndicator)) {
                return getSeriesForIndicatorKey(seriesCache, chartIndicator, activeZScoreYears);
              }
              switch (chartIndicator) {
                case "ev_ebitda": return seriesCache.evYield || [];
                case "div_yield": return seriesCache.divYield || [];
                case "peg_ratio": return seriesCache.pegQ || [];
                case "fgpz": return seriesCache.fgpz || [];
                case "gag": return seriesCache.gag || [];
                case "syo": return seriesCache.syo || [];
                case "rmo": return seriesCache.rmo || [];
                case "tamo": return seriesCache.tamo || [];
                case "nimo": return seriesCache.nimo || [];
                case "vmz": return seriesCache.vmz || [];
                case "oey": return seriesCache.oey || [];
                case "vmo": return seriesCache.vmo || [];
                case "ed": return seriesCache.ed || [];
                case "vddm": return seriesCache.vddm || [];
                case "nupl": return seriesCache.nupl || [];
                case "realized_price_252": return seriesCache.realizedPrice252 || [];
                case "realized_price_504": return seriesCache.realizedPrice504 || [];
                case "realized_price_5y": return seriesCache.realizedPrice5y || [];
                case "risk": return seriesCache.risk || [];
                case "srm": return seriesCache.srm || [];
                case "mrvz": return seriesCache.mrvz || [];
                case "pe_peratio": return seriesCache.pePERatio || [];
                case "forward_pe": return seriesCache.forwardPe || [];
        case "ttm_pe": return seriesCache.ttmPe || [];
        case "revenue_cagr": return seriesCache.revenueCagr2y || seriesCache.revenueCagr || [];
        case "eps_cagr": return seriesCache.epsCagr2y || seriesCache.epsCagr || [];
                case "rev_val": return (seriesCache.revVal?.series || []).map(p => ({ time: p.time, value: p.value }));
                case "eps_val": return (seriesCache.epsVal?.series || []).map(p => ({ time: p.time, value: p.value }));
                default: return [];
              }
            };
            return (pickBottom() || []).filter(p => p && Number.isFinite(p.time) && Number.isFinite(p.value));
          })()}
          pricePoints={fundamentalsData.pxForMath}
          onResult={setCustomStrategyResult}
        />
      )}
    </div>
  );
}

/* -----------------------------
   Backtest stat column tooltips (info icon on hover)
--------------------------------*/
const STAT_COL_TOOLTIPS = {
  "n": "Number of completed instances: each entry signal (cross, DCA episode start, or single leading edge) is simulated as its own trade through exit or max-holding cap. Overlapping periods are allowed; changing the cap only changes each trade’s exit date, not how many entry signals exist.",
  "Avg %": "Average percentage return per trade (arithmetic mean).",
  "Geo Avg %": "Geometric mean of percentage returns; approximates compound return per trade.",
  "Std %": "Standard deviation of trade returns (volatility).",
  "Avg Days": "Average number of days between entry and exit per trade.",
  "Win-to-Loss Ratio": "Ratio of winning trades (return > 0%) to losing trades (return < 0%).",
  "Sharpe Ratio": "Return per unit of risk (mean return ÷ standard deviation). Uses 0% risk-free rate per trade.",
};

/* -----------------------------
   Render table block (kept)
--------------------------------*/
function IndicatorCalcBlock({ calc }) {
  const [hoveredCol, setHoveredCol] = useState(null);
  if (!calc) return null;
  return (
    <div style={{ marginTop:12, padding:12, background: BRAND.panel, border:`1px solid ${BRAND.border}`, borderRadius:10, color: BRAND.text }}>
      <div style={{ fontWeight:700, marginBottom:8 }}>{calc.title}</div>
      {calc.rows?.length ? (
        <div style={{ overflowX:"auto" }}>
          <table style={{ borderCollapse:"collapse", width:"100%" }}>
            <thead style={{ background:"#2f2f34", color: BRAND.text }}>
              <tr>
                {calc.columns.map(c => {
                  const tip = STAT_COL_TOOLTIPS[c];
                  return (
                    <th key={c} style={{ textAlign:"left", padding:8, borderBottom:`1px solid ${BRAND.border}`, whiteSpace:"nowrap", position:"relative", verticalAlign:"middle" }}>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                        {c}
                        {tip != null && (
                          <span
                            onMouseEnter={() => setHoveredCol(c)}
                            onMouseLeave={() => setHoveredCol(null)}
                            style={{
                              display:"inline-flex",
                              alignItems:"center",
                              justifyContent:"center",
                              width:16,
                              height:16,
                              borderRadius:"50%",
                              border:`1px solid ${BRAND.muted}`,
                              color: BRAND.muted,
                              fontSize:11,
                              fontWeight:700,
                              cursor:"help",
                              flexShrink:0,
                            }}
                            aria-label="Info"
                          >
                            i
                          </span>
                        )}
                      </span>
                      {hoveredCol === c && tip != null && (
                        <div
                          onMouseEnter={() => setHoveredCol(c)}
                          onMouseLeave={() => setHoveredCol(null)}
                          style={{
                            position:"absolute",
                            left:0,
                            top:"100%",
                            marginTop:4,
                            zIndex:10,
                            minWidth:220,
                            maxWidth:320,
                            width:"max-content",
                            padding:10,
                            background: BRAND.bgSecondary,
                            border:`1px solid ${BRAND.border}`,
                            borderRadius:8,
                            fontSize:13,
                            lineHeight:1.4,
                            color: BRAND.text,
                            boxShadow:"0 4px 12px rgba(0,0,0,0.3)",
                            whiteSpace:"normal",
                            wordBreak:"normal",
                            overflow:"visible",
                          }}
                        >
                          {tip}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {calc.rows.map((r, idx) => r && (
                <tr key={idx} style={{ color: BRAND.text }}>
                  {calc.columns.map(c => (
                    <td key={c} style={{ padding:8, borderBottom:`1px solid ${BRAND.border}` }}>{r[c] ?? "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div>—</div>}
    </div>
  );
}
function IndicatorDescriptionBlock({ indicatorKey, zScoreYears }) {
  if (!indicatorKey) return null;
  const meta = getIndicatorDescription(indicatorKey, { zScoreYears });
  if (!meta) return null;

  const pill = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    border: `1px solid ${BRAND.border}`,
    fontSize: 14,
    color: BRAND.text,
    marginRight: 6,
    marginBottom: 4,
  };

  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: BRAND.panel,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 10,
        color: BRAND.text,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 10}}>
        {meta.title}
      </div>

      {meta.short && (
        <div style={{ marginBottom: 12, color: BRAND.text, fontSize: 18 }}>
          {meta.short}
        </div>
      )}

      <div style={{ 
        display: "grid", 
        gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "1.2fr 1.2fr", 
        gap: 16 
      }}>
        <div>
          {meta.intuition && (
            <>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Intuition</div>
              <div style={{ fontSize: 16, color: BRAND.text }}>{meta.intuition}</div>
            </>
          )}
          {meta.how && (
            <>
              <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 8 }}>How it’s calculated</div>
              <div style={{ fontSize: 16, color: BRAND.text }}>{meta.how}</div>
            </>
          )}
          {meta.when && (
            <>
              <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 8 }}>When to use / avoid</div>
              <div style={{ fontSize: 16, color: BRAND.text }}>{meta.when}</div>
            </>
          )}
        </div>

        <div>
          {(meta.pros?.length || meta.cons?.length) && (
            <div style={{ display: "flex", gap: 16 }}>
              {meta.pros?.length ? (
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Advantages</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 16, color: BRAND.text }}>
                    {meta.pros.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              ) : null}
              {meta.cons?.length ? (
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Disadvantages</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 16, color: BRAND.text }}>
                    {meta.cons.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              ) : null}
            </div>
          )}

          {meta.pairWith?.length ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Pairs well with</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {meta.pairWith.map((k) => (
                  <span key={k} style={pill}>
                    {INDICATOR_OPTIONS[k] || k}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
